<template>
  <div id="insightsDashboard" class="divContainer">
    <div class="divContentInsights margin-subpages">
      <div class="top-contariner flex">
        <div class="engagement">
          <div class="fs-15 fw-700 flex-center">
            {{ $t("insights.Engagement_and_Impact") }}
            <el-tooltip class="header-table-tooltip" placement="top">
              <div slot="content" class="p-5">
                <div style="max-width: 250px">
                  {{ $t("insights.Highlighting_key_engagement_metrics") }}
                </div>
              </div>
              <img :src="$utils('icons', 'ic_help_outline-16px')" class="ml-6 cursor-pointer"
                alt="icons_ic_help_outline-16px" />
            </el-tooltip>
            <div class="flex-center"></div>
          </div>
          <div class="card-contariner">
            <div class="flex">
              <div class="engagement-left">
                <div class="section-header">
                  <span class="flex-center router-link section-title" @click="routerLink('insightsWithTab', 'people')">
                    <span>People Impact</span>
                    <i class="el-icon-arrow-right"></i>
                  </span>
                </div>
                <div v-if="engagementImpactLoadingStatus">
                  <div class="" style="height: 50px"></div>
                  <div class="fs-10 mt-2 text-skeleton" style="width: 90%">
                    &nbsp;
                  </div>
                </div>
                <div v-else>
                  <el-tooltip placement="top" effect="light">
                    <div slot="content" style="max-width: 320px; padding: 12px;">
                      <div class="fw-700 fs-13 mb-2">Workplace Culture Impact</div>
                      <div class="fs-12 mb-3 opa-08">
                        Based on behavioral analytics and individual contributions to workplace culture
                      </div>
                      <div class="fs-12">
                        <div class="tooltip-legend-item">
                          <span class="tooltip-color bg-green"></span>
                          <div>
                            <strong>High Impact</strong>
                            <div class="fs-11 opa-06">Actively drives positive culture, recognizes others, consistently engaged</div>
                            <div class="fs-11 opa-08 mt-1">{{ engagementpeople.counts.green }} people ({{ Math.round(engagementpeople.counts.green / engagementpeople.total * 100) }}%)</div>
                          </div>
                        </div>
                        <div class="tooltip-legend-item">
                          <span class="tooltip-color bg-yellow"></span>
                          <div>
                            <strong>Moderate Impact</strong>
                            <div class="fs-11 opa-06">Steady contributor, participates regularly, supports others</div>
                            <div class="fs-11 opa-08 mt-1">{{ engagementpeople.counts.yellow }} people ({{ Math.round(engagementpeople.counts.yellow / engagementpeople.total * 100) }}%)</div>
                          </div>
                        </div>
                        <div class="tooltip-legend-item">
                          <span class="tooltip-color bg-red"></span>
                          <div>
                            <strong>Low Impact</strong>
                            <div class="fs-11 opa-06">Limited engagement, may benefit from additional support</div>
                            <div class="fs-11 opa-08 mt-1">{{ engagementpeople.counts.red }} people ({{ Math.round(engagementpeople.counts.red / engagementpeople.total * 100) }}%)</div>
                          </div>
                        </div>
                      </div>
                      <div class="tooltip-footer">
                        <div class="fs-12 opa-04 mt-8 pt-2" style="border-top: 1px solid rgba(0,0,0,0.1);">
                          <em>Target: ≥70% combined high + moderate impact for optimal performance</em>
                        </div>
                      </div>
                    </div>
                    <div class="stacked-bar-container">
                      <div class="stacked-bar">
                        <div 
                          v-if="engagementpeople.counts.green > 0"
                          class="bar-segment bar-green" 
                          :style="`width: ${(engagementpeople.counts.green / engagementpeople.total * 100)}%`"
                          :title="`${engagementpeople.counts.green} high impact contributors (${Math.round(engagementpeople.counts.green / engagementpeople.total * 100)}%)`">
                        </div>
                        <div 
                          v-if="engagementpeople.counts.yellow > 0"
                          class="bar-segment bar-yellow" 
                          :style="`width: ${(engagementpeople.counts.yellow / engagementpeople.total * 100)}%`"
                          :title="`${engagementpeople.counts.yellow} medium impact contributors (${Math.round(engagementpeople.counts.yellow / engagementpeople.total * 100)}%)`">
                        </div>
                        <div 
                          v-if="engagementpeople.counts.red > 0"
                          class="bar-segment bar-red" 
                          :style="`width: ${(engagementpeople.counts.red / engagementpeople.total * 100)}%`"
                          :title="`${engagementpeople.counts.red} low impact contributors (${Math.round(engagementpeople.counts.red / engagementpeople.total * 100)}%)`">
                        </div>
                      </div>
                      <div class="bar-legend">
                        <div class="legend-item">
                          <div class="legend-color bg-green"></div>
                          <span class="legend-text">{{ engagementpeople.counts.green }}</span>
                        </div>
                        <div class="legend-item">
                          <div class="legend-color bg-yellow"></div>
                          <span class="legend-text">{{ engagementpeople.counts.yellow }}</span>
                        </div>
                        <div class="legend-item">
                          <div class="legend-color bg-red"></div>
                          <span class="legend-text">{{ engagementpeople.counts.red }}</span>
                        </div>
                      </div>
                    </div>
                  </el-tooltip>
                  <div class="fs-11 mt-2 metric-summary">
                    <span :class="getPeopleImpactClass()">{{ getPeopleImpactText() }}</span>
                  </div>
                </div>
              </div>
              <div class="engagement-right">
                <div class="section-header">
                  <span class="flex-center router-link section-title" @click="routerLink('insightsWithTab', 'managers')">
                    <span>Manager Activity</span>
                    <i class="el-icon-arrow-right"></i>
                  </span>
                </div>
                <div v-if="engagementImpactLoadingStatus">
                  <div class="" style="height: 50px"></div>
                  <div class="fs-10 mt-2 text-skeleton" style="width: 90%">
                    &nbsp;
                  </div>
                </div>
                <div v-else>
                  <el-tooltip placement="top" effect="light">
                    <div slot="content" style="max-width: 320px; padding: 12px;">
                      <div class="fw-700 fs-13 mb-2">Manager Feedback Responsiveness</div>
                      <div class="fs-12 mb-3 opa-08">
                        Based on feedback reply rates to their teams
                      </div>
                      <div class="fs-12">
                        <div class="tooltip-legend-item">
                          <span class="tooltip-color bg-green"></span>
                          <div>
                            <strong>Responsive</strong>
                            <div class="fs-11 opa-06">Consistently replies to team feedback to keep their team engaged</div>
                            <div class="fs-11 opa-08 mt-1">{{ activeOverview.active }} managers ({{ Math.round(activeOverview.active / activeOverview.all * 100) }}%)</div>
                          </div>
                        </div>
                        <div class="tooltip-legend-item">
                          <span class="tooltip-color bg-yellow"></span>
                          <div>
                            <strong>Moderate</strong>
                            <div class="fs-11 opa-06">Regular replies but may miss some feedback</div>
                            <div class="fs-11 opa-08 mt-1">{{ activeOverview.passive }} managers ({{ Math.round(activeOverview.passive / activeOverview.all * 100) }}%)</div>
                          </div>
                        </div>
                        <div class="tooltip-legend-item">
                          <span class="tooltip-color bg-red"></span>
                          <div>
                            <strong>Minimal</strong>
                            <div class="fs-11 opa-06">Infrequent replies, may need coaching on feedback</div>
                            <div class="fs-11 opa-08 mt-1">{{ activeOverview.inactive }} managers ({{ Math.round(activeOverview.inactive / activeOverview.all * 100) }}%)</div>
                          </div>
                        </div>
                      </div>
                      <div class="tooltip-footer">
                        <div class="fs-12 opa-04 mt-8 pt-2" style="border-top: 1px solid rgba(0,0,0,0.1);">
                          <em>Target: ≥80% manager reply rate for optimal performance</em>
                        </div>
                      </div>
                    </div>
                    <div class="stacked-bar-container">
                      <div class="stacked-bar">
                        <div 
                          v-if="activeOverview.active > 0"
                          class="bar-segment bar-green" 
                          :style="`width: ${(activeOverview.active / activeOverview.all * 100)}%`"
                          :title="`${activeOverview.active} active managers (${Math.round(activeOverview.active / activeOverview.all * 100)}%)`">
                        </div>
                        <div 
                          v-if="activeOverview.passive > 0"
                          class="bar-segment bar-yellow" 
                          :style="`width: ${(activeOverview.passive / activeOverview.all * 100)}%`"
                          :title="`${activeOverview.passive} passive managers (${Math.round(activeOverview.passive / activeOverview.all * 100)}%)`">
                        </div>
                        <div 
                          v-if="activeOverview.inactive > 0"
                          class="bar-segment bar-red" 
                          :style="`width: ${(activeOverview.inactive / activeOverview.all * 100)}%`"
                          :title="`${activeOverview.inactive} inactive managers (${Math.round(activeOverview.inactive / activeOverview.all * 100)}%)`">
                        </div>
                      </div>
                      <div class="bar-legend">
                        <div class="legend-item">
                          <div class="legend-color bg-green"></div>
                          <span class="legend-text">{{ activeOverview.active }}</span>
                        </div>
                        <div class="legend-item">
                          <div class="legend-color bg-yellow"></div>
                          <span class="legend-text">{{ activeOverview.passive }}</span>
                        </div>
                        <div class="legend-item">
                          <div class="legend-color bg-red"></div>
                          <span class="legend-text">{{ activeOverview.inactive }}</span>
                        </div>
                      </div>
                    </div>
                  </el-tooltip>
                  <div class="fs-11 mt-2 metric-summary">
                    <span :class="getManagerActivityClass()">{{ getManagerActivityText() }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="x-line my-12"></div>
            <div v-if="engagementImpactLoadingStatus">
              <div class="flex-center">
                <div class="fs-13 fw-700 mr-auto">
                  {{ $t("insights.eNPS") }}
                </div>
                <div class="mt-6 fs-10 text-skeleton" style="width: 30%; background: #6c30cc10">
                  &nbsp;
                </div>
              </div>
              <div class="flex-center mt-12">
                <div class="benchmark mt-12">&nbsp;</div>
                <div class="mx-auto" style="width: 74px">
                  <div class="flex-center text-skeleton" style="width: 80%">
                    &nbsp;
                  </div>
                  <div class="fs-11 mt-6 text-skeleton opa-06" style="width: 100%">
                    &nbsp;
                  </div>
                </div>
                <div class="mx-auto" style="width: 62px">
                  <div class="flex-center text-skeleton" style="width: 80%">
                    &nbsp;
                  </div>
                  <div class="fs-11 mt-6 text-skeleton opa-06" style="width: 100%">
                    &nbsp;
                  </div>
                </div>
                <div class="mx-auto" style="width: 73px">
                  <div class="flex-center text-skeleton" style="width: 80%">
                    &nbsp;
                  </div>
                  <div class="fs-11 mt-6 text-skeleton opa-06" style="width: 100%">
                    &nbsp;
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              <div class="flex-center">
                <div class="fs-13 fw-700 mr-auto">
                  {{ this.benchmarkBreak }}
                </div>
                <span class="flex-center router-link" @click="routerLink('insightsWithTab', 'report')">
                  <span>{{ $t("insights.Report_Card") }}</span>
                  <i class="el-icon-arrow-right"></i>
                </span>
              </div>

              <div v-if="enps.type == 'eNPS' || enps.type == 'NPS'" class="flex-center mt-12">
                <div :class="`benchmark ${enps.total < benchmarkScoreRed()
                  ? 'red'
                  : enps.total < benchmarkScore()
                    ? 'yellow'
                    : 'green'
                  }`">
                  <el-tooltip 
                    placement="top" 
                    effect="light"
                    :visible-arrow="true"
                    popper-class="score-distribution-tooltip">
                    <div slot="content" style="padding: 10px 40px 10px 20px; min-width: 340px;">
                      <div class="tooltip-title">
                        {{ $t("insights.Score_Distribution") }}
                        <span class="opa-04" style="float: right; margin-right: -20px;">
                          {{ getTotalResponses() }} responses
                        </span>
                      </div>
                      <div class="horizontal-histogram">
                        <div class="score-labels">
                          <div v-for="score in getScoreRange()" :key="score" class="score-label-item">
                            {{ formatScoreLabel(score) }}
                          </div>
                        </div>
                        <div class="histogram-bars">
                          <div v-for="score in getScoreRange()" :key="score" class="histogram-bar" 
                               :style="{
                                 height: getScoreWidth(score),
                                 backgroundColor: getScoreColor(score)
                               }">
                          </div>
                        </div>
                        <div class="score-counts">
                          <div v-for="score in getScoreRange()" :key="score" class="score-count-item">
                            ({{getScoreCount(score)}})
                          </div>
                        </div>
                      </div>
                    </div>
                    <span>{{ enps.total > 0 ? "+" + enps.total : enps.total }}</span>
                  </el-tooltip>
                </div>
                <div class="mx-auto">
                  <div class="flex-center fs-14 fw-700">
                    {{ enps.percent ? enps.percent.promotors : 0 }}%
                    <img class="ml-6" :src="$utils('icons', 'Promoters_16')" alt="icons_Promoters_16" />
                  </div>
                  <div class="fs-11 fw-700 opa-06 mt-4">
                    {{ $t("insights.Promoters") }} ({{
                      enps.promotors ? enps.promotors : 0
                    }})
                  </div>
                </div>
                <div class="mx-auto">
                  <div class="flex-center fs-14 fw-700">
                    {{ enps.percent ? enps.percent.passives : 0 }}%
                    <img class="ml-6" :src="$utils('icons', 'Passives_16')" alt="icons_Passives_16" />
                  </div>
                  <div class="fs-11 fw-700 opa-06 mt-4">
                    {{ $t("insights.Passives") }} ({{
                      enps.passives ? enps.passives : 0
                    }})
                  </div>
                </div>
                <div class="mx-auto">
                  <div class="flex-center fs-14 fw-700">
                    {{ enps.percent ? enps.percent.detractors : 0 }}%
                    <img class="ml-6" :src="$utils('icons', 'Distractors_16')" alt="icons_Distractors_16" />
                  </div>
                  <div class="fs-11 fw-700 opa-06 mt-4">
                    {{ $t("insights.Detractors") }} ({{
                      enps.detractors ? enps.detractors : 0
                    }})
                  </div>
                </div>
              </div>
              <div v-else class="flex-center mt-12">
                <div :class="`benchmark ${enps.total < benchmarkScoreRed()
                    ? 'red'
                    : enps.total < benchmarkScore()
                      ? 'yellow'
                      : 'green'
                  }`">
                  <span v-if="checkedInxed">{{
                    enps.total > 0 ? enps.total : enps.total
                  }}</span>
                  <span v-else>{{
                    enps.total > 0 ? "+" + enps.total : enps.total
                  }}</span>
                </div>
                <div class="mx-auto">
                  <div class="flex-center fs-14 fw-700">
                    {{ enps.percent ? enps.percent.excellent : 0 }}%
                    <img class="ml-6" :src="$utils('icons', imagesBenchmark('Excellent'))" alt="icons_Promoters_16" />
                  </div>
                  <div class="fs-11 fw-700 opa-06 mt-4">
                    {{ $t("insights.Excellent") }} ({{
                      enps.excellent ? enps.excellent : 0
                    }})
                  </div>
                </div>
                <div class="mx-auto">
                  <div class="flex-center fs-14 fw-700">
                    {{ enps.percent ? enps.percent.fair : 0 }}%
                    <img class="ml-6" :src="$utils('icons', imagesBenchmark('Fair'))" alt="icons_Passives_16" />
                  </div>
                  <div class="fs-11 fw-700 opa-06 mt-4">
                    {{ $t("insights.Fair") }} ({{ enps.fair ? enps.fair : 0 }})
                  </div>
                </div>
                <div class="mx-auto">
                  <div class="flex-center fs-14 fw-700">
                    {{ enps.percent ? enps.percent.critical : 0 }}%
                    <img class="ml-6" :src="$utils('icons', imagesBenchmark('Critical'))" alt="icons_Distractors_16" />
                  </div>
                  <div class="fs-11 fw-700 opa-06 mt-4">
                    {{
                      benchmarkBreak == "WHO-5 Wellbeing Index"
                      ? $t("insights.Poor")
                      : $t("insights.Critical")
                    }}
                    ({{ enps.critical ? enps.critical : 0 }})
                  </div>
                </div>
              </div>

              <div class="flex-center fs-12 mt-16">
                <div v-if="enps.total == benchmarkScore()">
                  <span class="fc-000000 fw-400">
                    {{ $t("insights.point_same_benchmark") }} (+{{
                      benchmarkScore()
                    }})</span>
                </div>
                <div v-else>
                  <span class="fw-900 fc-1AC362" v-if="enps.total > benchmarkScore()">{{ enps.total - benchmarkScore() }}
                    {{ $t("insights.points") }}

                    <span class="fc-000000 fw-400">{{ $t("insights.above_benchmark") }} (<span
                        v-if="!checkedInxed">+</span>{{ benchmarkScore() }})</span>
                  </span>
                  <span v-else class="fw-900 fc-F44336">{{
                    isNaN(enps.total)
                    ? 0
                    : (enps.total - benchmarkScore()) * -1
                  }}
                    {{ $t("insights.points") }}

                    <span class="fc-000000 fw-400">{{ $t("insights.below_benchmark") }} (<span
                        v-if="!checkedInxed">+</span>{{ benchmarkScore() }})</span>
                  </span>
                </div>
                <div class="ml-auto">
                  <div class="point-bar">
                    <div class="value" :style="`width: ${benchmarkBar(enps.total)}px; ${enps.total < 0
                      ? ' background-color: #F44336;'
                      : enps.total < benchmarkScore()
                        ? ' background-color: #FBC02D;'
                        : ''
                      }`"></div>
                    <div class="value benchmark" :style="`width: ${benchmarkBar(benchmarkScore())}px;`"></div>
                  </div>
                  <div v-if="checkedInxed" class="flex-center opa-04 fs-10 fw-700 mt-2">
                    <div>0</div>
                    <div class="ml-auto">100</div>
                  </div>
                  <div v-else class="flex-center opa-04 fs-10 fw-700 mt-2">
                    <div>-100</div>
                    <div class="ml-auto">+100</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="weekly-status">
          <div class="fs-15 fw-700">{{ dateshow }}</div>
          <div class="card-contariner">
            <div class="flex">
              <div>
                <div class="fs-12 fw-700" style="width: 96px">
                  {{ $t("insights.Happiness_Rate") }}
                </div>
                <div v-if="weeklyLoadingStatus" class="flex-center mt-6">
                  <div class="image-skeleton opa-08" style="width: 24px; height: 24px; border-radius: 24px">
                    &nbsp;
                  </div>
                  <div class="text-skeleton ml-6" style="width: 40px">
                    &nbsp;
                  </div>
                </div>
                <div v-else class="flex-center mt-6">
                  <img :src="$utils('icons', 'Happiness_Rate_24')" alt="icons_Happiness_Rate_24" />
                  <div class="flex-center">
                    <div class="fs-16 fw-700 ml-6">
                      {{ `${happiness.happy}%` }}
                    </div>
                    <div v-if="happiness.calculatehappy != 0" :class="`label-change ${0 > happiness.calculatehappy ? 'fc-F44336' : 'fc-1AC362'
                      }`">
                      {{
                        happiness.calculatehappy > 0
                        ? `+${happiness.calculatehappy}`
                        : `${happiness.calculatehappy}`
                      }}
                      pts
                    </div>
                    <div v-else :class="`label-change fc-6A6A6A`">
                      {{ `+${happiness.calculatehappy}` }} pts
                    </div>
                  </div>
                </div>
              </div>
              <div class="ml-12">
                <div class="fs-12 fw-700">{{ $t("insights.Risk_Rate") }}</div>
                <div v-if="weeklyLoadingStatus" class="flex-center mt-6">
                  <div class="image-skeleton opa-08" style="width: 24px; height: 24px; border-radius: 24px">
                    &nbsp;
                  </div>
                  <div class="text-skeleton ml-6" style="width: 40px">
                    &nbsp;
                  </div>
                </div>
                <div v-else class="flex-center mt-6">
                  <img :src="$utils('icons', 'Risk_Rate_v3_24')" alt="icons_Risk_Rate_v3_24" />
                  <div class="fs-16 fw-700 ml-6">
                    {{ `${happiness.worse}%` }}
                  </div>
                  <div v-if="happiness.calculaterisk != 0" :class="`label-change ${happiness.calculaterisk * 1 > 0
                    ? 'fc-F44336'
                    : 'fc-1AC362'
                    }`">
                    {{
                      happiness.calculaterisk > 0
                      ? `+${happiness.calculaterisk}`
                      : `${happiness.calculaterisk}`
                    }}
                    pts
                  </div>
                  <div v-else :class="`label-change fc-1AC362`">
                    {{ `+${happiness.calculaterisk}` }} pts
                  </div>
                </div>
              </div>
            </div>
            <div v-if="weeklyLoadingStatus" class="text-skeleton fs-12 mt-12" style="width: 95px; background: #6c30cc10">
              &nbsp;
            </div>
            <span v-else class="flex-center router-link mt-12" @click="routerLink('insightsWithTab', 'teams')">
              <span>{{ $t("insights.View_by_team") }}</span>
              <i class="el-icon-arrow-right"></i>
            </span>
            <div class="x-line my-12"></div>
            <div class="mt-24">
              <div class="fs-12 fw-700">
                {{ $t("insights.Open_ended_Feedback") }}
              </div>
              <div class="fs-12">
                <div v-if="weeklyLoadingStatus" class="flex-center mt-6">
                  <div class="image-skeleton opa-08" style="width: 24px; height: 24px; border-radius: 24px">
                    &nbsp;
                  </div>
                  <div class="text-skeleton ml-6" style="width: 40px">
                    &nbsp;
                  </div>
                </div>
                <div v-else class="flex-center my-6">
                  <img width="32" :src="$utils('icons', 'Open-ended_Feedback_24@2x')"
                    alt="icons_Open-ended_Feedback_24@2x" />
                  <div class="fs-16 fw-700 ml-6">
                    {{ `${feedback.feedback}%` }}
                  </div>
                  &nbsp;
                  <div v-if="feedback.calculate != 0" :class="`label-change ${feedback.calculate > 0 ? 'fc-1AC362' : 'fc-F44336'
                    }`">
                    {{
                      feedback.calculate > 0
                      ? `+${feedback.calculate}`
                      : `${feedback.calculate}`
                    }}
                    pts
                  </div>
                  <div v-else :class="`label-change fc-6A6A6A`">
                    {{ `+${feedback.calculate}` }} pts
                  </div>
                </div>
                <div v-if="weeklyLoadingStatus" class="text-skeleton mt-6" style="width: 140px">
                  &nbsp;
                </div>
                <div v-else>
                  <div v-if="feedback.feedback - 40 >= 0">
                    <span class="fw-900 fc-1AC362">{{ feedback.feedback - 40 }}% {{ $t("insights.points") }}
                    </span>
                    <span>{{ $t("insights.above_benchmark") }} (40%)</span>
                  </div>
                  <div v-else>
                    <span class="fw-900 fc-F44336">{{
                      isNaN(feedback.feedback)
                      ? "-"
                      : (feedback.feedback - 40) * -1
                    }}% {{ $t("insights.points") }}
                    </span>
                    <span>{{ $t("insights.below_benchmark") }} (40%)</span>
                  </div>
                </div>
              </div>
            </div>
            <span v-if="!weeklyLoadingStatus" class="flex-center router-link mt-12" @click="
              routerLink('questions', 'standard', questionOpenEnd.question_id)
              ">
              <span class="relative">
                <div class="ellipsis-3">{{ questionOpenEnd.question_en }}</div>
              </span>
              <i class="el-icon-arrow-right ml-auto"></i>
            </span>
          </div>
        </div>

        <div class="activity">
          <div class="fs-15 fw-700">{{ $t("insights.Activity") }}</div>
          <div class="card-contariner">
            <div class="flex-center">
              <div class="fs-12 fw-700 mr-auto">
                {{ $t("insights.Adoption") }}
              </div>
              <div v-if="activityLoadingStatus" class="text-skeleton fs-12" style="width: 60px; background: #6c30cc10">
                &nbsp;
              </div>
              <span v-else class="flex-center router-link" @click="routerLink('members', 'members')">
                <span>{{ $t("insights.Members") }}</span>
                <i class="el-icon-arrow-right"></i>
              </span>
            </div>
            <div class="mt-6">
              <div v-if="activityLoadingStatus" class="bar-contariner flex-center opa-06">
                <div v-for="(value, index) in [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]" :key="index + '_bar_mock_one'"
                  :class="`bar ${value}`"></div>
                <div class="text-skeleton ml-6" style="width: 40px">&nbsp;</div>
              </div>
              <div v-else class="bar-contariner flex-center">
                <div v-for="(value, index) in arrayToPercentage(adoption)" :key="index + '_bar_adoption'"
                  :class="`bar ${value}`"></div>
                <div class="fs-16 fw-700 ml-6">{{ adoption }}%</div>
              </div>
            </div>
            <div v-if="activityLoadingStatus" class="text-skeleton mt-6 fs-12 opa-08" style="width: 180px">
              &nbsp;
            </div>
            <div v-else class="mt-6 fs-12">
              <div v-if="adoption - 90 >= 0">
                <span class="fw-900 fc-1AC362">{{ adoption - 90 ? (adoption - 90).toFixed() : 0 }}%
                  {{ $t("insights.points") }}
                </span>
                <span class="fc-000000 fw-400">{{ $t("insights.above_benchmark") }} (90%)</span>
              </div>
              <div v-else>
                <span class="fw-900 fc-F44336">{{
                  (adoption - 90) * -1 ? ((adoption - 90) * -1).toFixed() : 0
                }}% {{ $t("insights.points") }}
                </span>
                <span class="fc-000000 fw-400">{{ $t("insights.below_benchmark") }} (90%)</span>
              </div>
            </div>

            <div class="x-line my-14"></div>

            <div class="flex-center mt-24">
              <div class="fs-12 fw-700 mr-auto">
                {{ $t("insights.Monthly_Active_Users") }}
              </div>
            </div>
            <div class="mt-6">
              <div v-if="activityLoadingStatus" class="bar-contariner flex-center opa-06">
                <div v-for="(value, index) in [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]" :key="index + '_bar_mock_one'"
                  :class="`bar ${value}`"></div>
                <div class="text-skeleton ml-6" style="width: 40px">&nbsp;</div>
              </div>
              <div v-else class="bar-contariner flex-center">
                <div v-for="(value, index) in arrayToPercentage(
                  monthlyActiveUser.bar
                )" :key="index + '_bar_adoption'" :class="`bar ${value}`"></div>
                <div class="fs-16 fw-700 ml-6">
                  {{ monthlyActiveUser.activeCurrent }}
                </div>
                <div v-if="monthlyActiveUser.percent_change != 0" :class="`label-change ${0 > monthlyActiveUser.percent_change
                  ? 'fc-F44336'
                  : 'fc-1AC362'
                  }`">
                  {{
                    monthlyActiveUser.percent_change
                    ? monthlyActiveUser.percent_change > 0
                      ? `+${monthlyActiveUser.percent_change}`
                      : monthlyActiveUser.percent_change
                    : 0
                  }}%
                </div>

                <div v-else :class="`label-change fc-6A6A6A`">
                  {{ `+${monthlyActiveUser.percent_change}` }}%
                </div>
              </div>
            </div>
            <div class="flex-center mt-16">
              <div class="fs-12 fw-700 mr-auto">
                {{ $t("insights.Weekly_Active_Users") }}
              </div>
            </div>
            <div class="mt-6">
              <div v-if="activityLoadingStatus" class="bar-contariner flex-center opa-04">
                <div v-for="(value, index) in [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]" :key="index + '_bar_mock_one'"
                  :class="`bar ${value}`"></div>
                <div class="text-skeleton ml-6" style="width: 30px">&nbsp;</div>
              </div>
              <div v-else class="bar-contariner flex-center">
                <div v-for="(value, index) in arrayToPercentage(
                  weeklyActiveUser.bar
                )" :key="index + '_bar_adoption'" :class="`bar ${value}`"></div>
                <div class="fs-16 fw-700 ml-6">
                  {{ weeklyActiveUser.activeCurrent }}
                </div>
                <div v-if="weeklyActiveUser.percent_change != 0" :class="`label-change ${0 > weeklyActiveUser.percent_change
                  ? 'fc-F44336'
                  : 'fc-1AC362'
                  }`">
                  {{
                    weeklyActiveUser.percent_change
                    ? weeklyActiveUser.percent_change > 0
                      ? `+${weeklyActiveUser.percent_change}`
                      : weeklyActiveUser.percent_change
                    : 0
                  }}%
                </div>
                <div v-else :class="`label-change fc-6A6A6A`">
                  {{ `+${weeklyActiveUser.percent_change}` }}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Focus, Goals & Learning Section -->
      <div class="focus-alignment-section mt-24">
        <!-- Section Headers Row -->
        <div class="section-headers-row">
          <!-- Focus & Goal Alignment Header -->
          <div class="section-header-group section-header-focus">
            <div class="flex">
              <span class="fs-15 fw-700">Focus & Goal Alignment</span>
              <el-tooltip effect="dark" placement="top">
                <div class="tooltipHelpStyle" slot="content">
                  <div style="margin-bottom: 8px;">Track how well team focus areas align with company goals.</div>
                  <div style="font-size: 11px; opacity: 0.8;">
                    <div>• Focus Coverage: Real-time snapshot</div>
                    <div>• Goal Alignment: Last 90 days</div>
                  </div>
                </div>
                <div style="padding-left: 8px; display: flex; align-items: center">
                  <img :src="getImgUrl('help_outline-16px')" />
                </div>
              </el-tooltip>
            </div>
          </div>
          <!-- Learning Header -->
          <div class="section-header-group section-header-learning">
            <div class="flex">
              <span class="fs-15 fw-700">Learning</span>
              <el-tooltip effect="dark" placement="top">
                <div class="tooltipHelpStyle" slot="content">
                  <div style="margin-bottom: 8px;">Track team learning and skill development progress.</div>
                  <div style="font-size: 11px; opacity: 0.8;">
                    <div>• Learning Progress: Past 6 months</div>
                  </div>
                </div>
                <div style="padding-left: 8px; display: flex; align-items: center">
                  <img :src="getImgUrl('help_outline-16px')" />
                </div>
              </el-tooltip>
            </div>
          </div>
        </div>
        <!-- Cards Row -->
        <div class="focus-alignment-cards">
          <!-- Focus Coverage Card -->
          <div class="focus-card">
            <div class="focus-card-header">
              <span class="fs-12 fw-700">Focus Coverage</span>
              <span class="flex-center router-link" @click="navigateToPerformance('focus-areas')">
                <span>Focus Areas</span>
                <i class="el-icon-arrow-right"></i>
              </span>
            </div>
            <div v-if="focusCoverageLoading" class="focus-card-loading">
              <div class="text-skeleton" style="width: 60px; height: 32px; margin-bottom: 8px;">&nbsp;</div>
              <div class="text-skeleton" style="width: 100%; height: 8px;">&nbsp;</div>
            </div>
            <div v-else class="focus-card-content">
              <div class="focus-metric">
                <span class="focus-metric-value" :style="{ color: focusCoverageHealthColor }">{{ focusCoverageRate }}%</span>
                <div v-if="focusCoverageWeekChange && focusCoverageWeekChange.trend !== 'no_data'" class="weekly-change-wrapper">
                  <div class="weekly-metric-row">
                    <span class="weekly-value">{{ focusCoverageWeekChange.current !== null ? Math.round(focusCoverageWeekChange.current) : 0 }}%</span>
                    <span 
                      class="label-change"
                      :class="{
                        'text-green': focusCoverageWeekChange.trend === 'up',
                        'text-red': focusCoverageWeekChange.trend === 'down',
                        'fc-6A6A6A': focusCoverageWeekChange.trend === 'neutral'
                      }"
                    >{{ focusCoverageWeekChange.change > 0 ? '+' : '' }}{{ focusCoverageWeekChange.change }}%</span>
                  </div>
                  <span class="change-label">Last week</span>
                </div>
              </div>
              <div class="focus-progress-bar">
                <div 
                  class="focus-progress-fill"
                  :style="{ 
                    width: Math.max(focusCoverageRate, 2) + '%', 
                    backgroundColor: focusCoverageHealthColor 
                  }"
                ></div>
              </div>
              <div class="focus-metric-label">
                {{ focusSummaryStats ? focusSummaryStats.withFocus : 0 }} of {{ focusSummaryStats ? focusSummaryStats.totalPeople : 0 }} people have focus
              </div>
            </div>
          </div>

          <!-- Goal Alignment Card -->
          <div class="focus-card">
            <div class="focus-card-header">
              <span class="fs-12 fw-700">Goal Alignment</span>
              <span class="flex-center router-link" @click="navigateToPerformance('goals')">
                <span>Goals</span>
                <i class="el-icon-arrow-right"></i>
              </span>
            </div>
            <div v-if="focusAlignmentLoading" class="focus-card-loading">
              <div class="text-skeleton" style="width: 60px; height: 32px; margin-bottom: 8px;">&nbsp;</div>
              <div class="text-skeleton" style="width: 100%; height: 8px;">&nbsp;</div>
            </div>
            <div v-else class="focus-card-content">
              <template v-if="isGoalAlignmentApplicable">
                <div class="focus-metric">
                  <span class="focus-metric-value" :style="{ color: goalAlignmentHealthColor }">{{ goalAlignmentRate }}%</span>
                  <div v-if="goalAlignmentWeekChange && goalAlignmentWeekChange.trend !== 'no_data'" class="weekly-change-wrapper">
                    <div class="weekly-metric-row">
                      <span class="weekly-value">{{ goalAlignmentWeekChange.current !== null ? Math.round(goalAlignmentWeekChange.current) : 0 }}%</span>
                      <span 
                        class="label-change"
                        :class="{
                          'text-green': goalAlignmentWeekChange.trend === 'up',
                          'text-red': goalAlignmentWeekChange.trend === 'down',
                          'fc-6A6A6A': goalAlignmentWeekChange.trend === 'neutral'
                        }"
                      >{{ goalAlignmentWeekChange.change > 0 ? '+' : '' }}{{ goalAlignmentWeekChange.change }}%</span>
                    </div>
                    <span class="change-label">Last week</span>
                  </div>
                </div>
                <div class="focus-progress-bar">
                  <div 
                    class="focus-progress-fill"
                    :style="{ 
                      width: Math.max(goalAlignmentRate, 2) + '%', 
                      backgroundColor: goalAlignmentHealthColor 
                    }"
                  ></div>
                </div>
                <div class="focus-metric-label">
                  {{ focusAlignmentData ? focusAlignmentData.alignedItems : 0 }} aligned / {{ focusAlignmentData ? focusAlignmentData.unalignedItems : 0 }} unaligned
                </div>
              </template>
              <template v-else>
                <div class="focus-metric">
                  <span class="focus-metric-value" style="color: #dcdfe6;">N/A</span>
                </div>
                <div class="focus-progress-bar">
                  <div class="focus-progress-fill" style="width: 0%;"></div>
                </div>
                <div class="focus-na-message">
                  <i class="el-icon-setting"></i>
                  <span>Set up goals to track alignment</span>
                </div>
              </template>
            </div>
          </div>

          <!-- Learning Progress Card -->
          <div class="focus-card">
            <div class="focus-card-header">
              <span class="fs-12 fw-700">Learning Progress</span>
              <span class="flex-center router-link" @click="navigateToSkills('learning-journeys')">
                <span>Learning Journeys</span>
                <i class="el-icon-arrow-right"></i>
              </span>
            </div>
            <div v-if="learningLoading" class="focus-card-loading">
              <div class="text-skeleton" style="width: 60px; height: 32px; margin-bottom: 8px;">&nbsp;</div>
              <div class="text-skeleton" style="width: 100%; height: 8px;">&nbsp;</div>
            </div>
            <div v-else class="focus-card-content">
              <div class="focus-metric">
                <span class="focus-metric-value" style="color: #6C30CC;">{{ learningOverview.upskilling }}</span>
              </div>
              <div class="focus-metric-label">
                members upskilling
              </div>
              <div class="focus-metric-label learning-stats">
                <span class="learning-stat">
                  <span class="learning-stat-value">{{ learningOverview.active }}</span> active
                </span>
                <span class="learning-stat-separator">·</span>
                <span class="learning-stat">
                  <span class="learning-stat-value" style="color: #35D077;">{{ learningOverview.completed }}</span> completed
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="grid-2-tenure mt-24">
        <div style="justify-content: flex-start">
          <div class="flex">
            <div>
              <span class="fs-15 fw-700">{{
                $t("insights.By_Job_Tenure")
              }}</span>
            </div>
            <el-tooltip effect="dark" placement="top">
              <div class="tooltipHelpStyle" slot="content">
                {{ $t("insights.A_breakdown_of_your") }}
              </div>
              <div style="padding-left: 8px; display: flex; align-items: center">
                <img :src="getImgUrl('help_outline-16px')" />
              </div>
            </el-tooltip>
          </div>
          <div class="flex-mid barchart-style relative">
            <div v-if="databarchartLoading" class="ml-auto absolute-mid">
              <loading-even></loading-even>
            </div>
            <div v-else-if="!databarchartLoading && !Object.keys(databarchart).length
              " class="opa-06 fs-13">
              Your tenure insights will appear after your first day with Happily. This helps us show you meaningful team patterns.
            </div>
            <div class="pt-20" style="height: 166px; width: 100%">
              <canvas height="166px" ref="barchart"></canvas>
            </div>
          </div>
        </div>
        <div class="past-12-hours ml-16">
          <div class="fs-15 fw-700">
            {{ $t("insights.Real_time_Activity") }}
          </div>
          <div class="card">
            <div class="fc-FFFFFF fw-700 fs-10 flex-center">
              <div class="mr-22">{{ $t("insights.PAST_12_HOURS") }}</div>
              <div>{{ $t("insights.BY_HOUR") }}</div>
            </div>
            <div class="flex">
              <div class="" style="font-size: 29px; width: 90px">
                <div class="mt-24">{{ maximumUsers }}</div>
              </div>
              <div style="height: 70px; width: 110px">
                <canvas ref="pastLinechart"></canvas>
              </div>
            </div>
            <div class="fs-10 fw-700 flex">
              <div>{{ $t("insights.TOP_ACTIVITIES") }}</div>
              <div class="ml-auto">{{ $t("insights.TIMES") }}</div>
            </div>
            <div class="line"></div>
            <div class="flex fs-11 mt-5" v-for="(item, index) in past12Hour" :key="index + '_list_' + item.label">
              <div class="fc-35D077 mr-4">+</div>
              <div>{{ item.label }}</div>
              <div class="ml-auto">{{ item.value }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-32">
        <div style="justify-content: flex-start">
          <div class="flex">
            <div>
              <span class="fs-15 fw-700">{{
                $t("insights.Engagement_Trend")
              }}</span>
            </div>
            <el-tooltip effect="dark" placement="top">
              <div class="tooltipHelpStyle" slot="content">
                {{ $t("insights.Engagement_Trend_Desc") }}
              </div>
              <div style="padding-left: 8px; display: flex; align-items: center">
                <img :src="getImgUrl('help_outline-16px')" />
              </div>
            </el-tooltip>
          </div>
        </div>
        <div class="grid-3-engagement mt-12">
          <div style="justify-content: flex-start">
            <div class="flex">
              <div>
                <div class="fs-12 chart-legend">
                  <div class="label">Overall</div>
                  <div class="symbol blue"></div>
                  <div class="score">{{ (databarchartEngagementScore['company']) ? databarchartEngagementScore['company'][databarchartEngagementScore['company'].length - 1] : '' }}</div>
                  <div :class="`tag ${detectColorTotal('company')}`">{{detectTextTotal('company')}}</div>
                </div>
              </div>
            </div>
            <div class="flex-mid barchart-engagement-style relative">
              <div v-if="EngagementScoreLoading" class="ml-auto absolute-mid">
                <loading-even></loading-even>
              </div>
              <div v-else-if="!EngagementScoreLoading && databarchartEngagementScore['company'] && !Object.keys(databarchartEngagementScore['company']).length
                " class="opa-06 ml-auto absolute-mid">
                Pending Data
              </div>
              <div class="" style="height: 80px; width: 100%">
                <canvas height="166px" ref="chartEngagementScoreCompany"></canvas>
              </div>
            </div>
          </div>

          <div style="justify-content: flex-start">
            <div class="flex">
              <div>
                <div class="fs-12 chart-legend">
                  <div class="label">Managers</div>
                  <div class="symbol orange"></div>
                  <div class="score">{{ (databarchartEngagementScore['manager']) ? databarchartEngagementScore['manager'][databarchartEngagementScore['manager'].length - 1] : '' }}</div>
                  <div :class="`tag ${detectColorTotal('manager')}`">{{detectTextTotal('manager')}}</div>
                </div>
              </div>
            </div>
            <div class="flex-mid barchart-engagement-style relative">
              <div v-if="EngagementScoreLoading" class="ml-auto absolute-mid">
                <loading-even></loading-even>
              </div>
              <div v-else-if="!EngagementScoreLoading && databarchartEngagementScore['manager'] && !Object.keys(databarchartEngagementScore['manager']).length
                " class="opa-06 ml-auto absolute-mid">
                Pending Data
              </div>
              <div class="" style="height: 80px; width: 100%">
                <canvas height="166px" ref="chartEngagementScoreManager"></canvas>
              </div>
            </div>
          </div>

          <div style="justify-content: flex-start">
            <div class="flex">
              <div>
                <div class="fs-12 chart-legend">
                  <div class="label">Leadership</div>
                  <div class="symbol pink"></div>
                  <div class="score">{{ (databarchartEngagementScore['leader']) ? databarchartEngagementScore['leader'][databarchartEngagementScore['leader'].length - 1]: '' }}</div>
                  <div :class="`tag ${detectColorTotal('leader')}`">{{detectTextTotal('leader')}}</div>
                </div>
              </div>
            </div>
            <div class="flex-mid barchart-engagement-style relative">
              <div v-if="EngagementScoreLoading" class="ml-auto absolute-mid">
                <loading-even></loading-even>
              </div>
              <div v-else-if="!EngagementScoreLoading && databarchartEngagementScore['leader'] && !Object.keys(databarchartEngagementScore['leader']).length
                " class="opa-06 ml-auto absolute-mid">
                Pending Data
              </div>
              <div class="" style="height: 80px; width: 100%">
                <canvas height="166px" ref="chartEngagementScoreLeader"></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- <div>
        <span class="fs-15 fw-700">{{
          $t("insights.Engagement_Score")
        }}</span>
      </div>
      <div class="grid-3-engagement mt-32">
      </div> -->

      <div style="justify-content: flex-end" class="mt-24">
        <div class="flex center" style="padding-bottom: 12px">
          <div>
            <span class="fs-15 fw-700">{{
              $t("insights.Key_Teams_and_Cohorts")
            }}</span>
          </div>
          <el-tooltip effect="dark" placement="top">
            <div class="tooltipHelpStyle" slot="content">
              {{ $t("insights.Highlighting_key_groups") }}
            </div>
            <div style="padding-left: 8px; display: flex; align-items: center">
              <img :src="getImgUrl('help_outline-16px')" />
            </div>
          </el-tooltip>
        </div>
        <div class="grid-5-20" v-if="datakeyGroupLoading">
          <div class="KeyGroupBox text-skeleton"></div>
          <div class="KeyGroupBox text-skeleton"></div>
          <div class="KeyGroupBox text-skeleton"></div>
          <div class="KeyGroupBox text-skeleton"></div>
          <div class="KeyGroupBox text-skeleton"></div>
          <div class="KeyGroupBox text-skeleton"></div>
          <div class="KeyGroupBox text-skeleton"></div>
        </div>
        <div class="grid-5-20" v-else>
          <div v-for="item in datakeygroup" :key="item.key">
            <el-tooltip effect="dark" placement="top" :disabled="item.keygroup.length < 16">
              <div class="tooltipKeyGroupStyle" 
              :class="[ blurMode ?  'blurelement': '' ]"
              slot="content">
                {{ item.keygroup }}
              </div>
              <div class="KeyGroupBox"
              :class="[ blurMode ?  'blurelement': '' ]">
                <div style="
                    padding-top: 16px;
                    padding-left: 14px;
                    position: absolute;
                  ">
                  <img v-if="item.emotion == 'bad'" :src="getImgUrlDim('ic_sad')" />
                  <img v-else-if="item.emotion == 'low_activity'" :src="getImgUrlDim('ic_low_activity')" />
                  <img v-else-if="item.emotion == 'low_feedback'" :src="getImgUrlDim('ic_low_feedback')" />
                  <img v-else :src="getImgUrlDim('ic_happy')" />
                </div>
                <div class="pt-4 pr-6" style="padding-left: 39px">
                  <span class="txtHeaderKey">{{
                    truncateTextKeyGroup(item.keygroup)
                  }}</span>
                </div>
                <div style="padding-left: 39px; margin-top: -5px">
                  <span class="txtDetailKey">{{ item.desc_text }}</span>
                </div>
              </div>
            </el-tooltip>
          </div>
        </div>
      </div>

      <div class="grid-2-second mt-32">
        <div>
          <div class="flex-center">
            <div class="fs-18 fw-700">{{ $t("insights.TONE_ANALYZER") }}</div>
            <!-- <span class="icon-beta">{{ $t("insights.BETA") }}</span> -->
          </div>
          <div v-if="!toneLoadingData">
            <div class="tone-body flex" v-for="(data, index) of toneData" :key="index + data.emoji">
              <div class="arrow flex-mid">
                <div class="flex-mid mt-8" v-if="data.rankingNow !== 0 && data.rankYesterdayDay !== 0">
                  <img v-if="data.rankingNow < data.rankYesterdayDay" src="../../assets/images/insights/ic_rank_up.png"
                    alt="" />
                  <img v-else-if="data.rankingNow > data.rankYesterdayDay"
                    src="../../assets/images/insights/ic_rank_down.png" class="mb-2" alt="" />
                  <img v-else src="../../assets/images/insights/ic_rank_same.png" alt="" />
                </div>
                <div class="flex-mid mt-8" v-else-if="data.rankYesterdayDay === 0">
                  <img src="../../assets/images/insights/ic_rank_up.png" alt="" />
                </div>
                <div class="flex-mid mt-6" v-else-if="data.rankingNow === 0">
                  <img src="../../assets/images/insights/ic_rank_down.png" alt="" />
                </div>
              </div>
              <div class="number fs-14 fw-500 opa-06">
                {{ data.rankingNow }}
              </div>
              <el-tooltip placement="top" effect="light">
                <div slot="content" class="tooltip-quality-detail" style="max-width: 280px;">
                  {{ data.details }}
                </div>
                <div class="emoji fs-14 fw-500 opa-09 ellipsis">
                  {{ data.emoji }} {{ data.emojiText }}
                </div>
              </el-tooltip>
              <div class="percentage-bar ellipsis">
                <el-progress :percentage="data.percentage" :show-text="false" color="#6c30cc"></el-progress>
              </div>
              <div class="percentage-number opa-09 fs-14">
                {{ data.percentage }}%
              </div>
              <div class="percentage-change-number" :class="isNegativeTone(data.toneId) ? (data.percentageChange < 0 ? 'up' : (data.percentageChange > 0 ? 'down' : '')) : (data.percentageChange >= 0 ? 'up' : 'down')">
                {{ data.percentageChange >= 0 ? "+" : "" }}
                {{ data.percentageChange }}%
              </div>
            </div>
          </div>
          <div v-else>
            <div class="tone-body text-skeleton" style="height: 20px; width: 320px"></div>
            <div class="tone-body text-skeleton" style="height: 20px; width: 320px"></div>
            <div class="tone-body text-skeleton" style="height: 20px; width: 320px"></div>
            <div class="tone-body text-skeleton" style="height: 20px; width: 220px"></div>
          </div>

          <!-- Communication Health as subsection -->
          <div v-if="!toneLoadingData" class="mt-24">
            <div class="fs-14 fw-600 mb-12">Communication Health</div>
            
            <!-- Gibberish Row -->
            <div class="tone-body flex">
              <div class="arrow flex-mid">
                <!-- No ranking arrow for quality checks -->
              </div>
              <div class="number fs-14 fw-500 opa-06">
                🗣️
              </div>
              <el-tooltip placement="top" effect="light" popper-class="quality-feedback-tooltip">
                <div slot="content" class="tooltip-quality-detail">
                  <div><strong>Gibberish Feedback:</strong> {{ gibbrishTone.count || 0 }} detected</div>
                  
                  <!-- Team breakdown -->
                  <div v-if="Object.keys(gibbrishByTeam).length > 0" class="mt-8">
                    <div class="flex-center mb-4">
                      <strong>Team Breakdown:</strong>
                      <span class="ml-auto fs-11 fc-6A6A6A">{{ Object.keys(gibbrishByTeam).length }} teams</span>
                    </div>
                    <div class="team-list-container">
                      <template v-for="(count, team, index) in sortedGibbrishByTeam">
                        <div v-if="showAllGibberishTeams || index < 5" :key="team" class="team-breakdown-item">
                          <span class="team-rank">{{ index + 1 }}</span>
                          <span class="team-name" :class="blurMode ? 'blurelement' : ''">{{ team }}</span>
                          <span class="team-count">{{ count }}</span>
                        </div>
                      </template>
                    </div>
                    <div v-if="Object.keys(gibbrishByTeam).length > 5" class="mt-8">
                      <el-button 
                        type="text" 
                        size="mini" 
                        @click.stop="showAllGibberishTeams = !showAllGibberishTeams"
                        class="expand-button">
                        {{ showAllGibberishTeams ? 'Show less' : `Show all ${Object.keys(gibbrishByTeam).length} teams` }}
                        <i :class="showAllGibberishTeams ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      </el-button>
                    </div>
                  </div>
                  
                  <div v-if="(gibbrishTone.count || 0) > 0" class="mt-8">
                    <strong>Actions:</strong>
                    <br>• Ask managers to uphold the quality of feedback
                    <br>• Consider simplifying survey questions
                  </div>
                  <div v-else class="mt-8 text-success">
                    ✓ All feedback is clear and understandable
                  </div>
                </div>
                <div class="emoji fs-14 fw-500 opa-09 ellipsis">
                  Gibberish
                </div>
              </el-tooltip>
              <div class="percentage-bar ellipsis">
                <!-- No progress bar for absolute counts -->
              </div>
              <div class="percentage-number opa-09 fs-14">
                {{ gibbrishTone.count || 0 }}
              </div>
              <div class="percentage-change-number" v-if="gibbrishTone.percentageChange !== undefined" :class="(gibbrishTone.percentageChange || 0) < 0 ? 'up' : ((gibbrishTone.percentageChange || 0) > 0 ? 'down' : '')">
                {{ (gibbrishTone.percentageChange || 0) < 0 ? '' : '+' }}{{ gibbrishTone.percentageChange || 0 }}%
              </div>
            </div>

            <!-- Toxic Row -->
            <div class="tone-body flex">
              <div class="arrow flex-mid">
                <!-- No ranking arrow for quality checks -->
              </div>
              <div class="number fs-14 fw-500 opa-06">
                ☠️
              </div>
              <el-tooltip placement="top" effect="light" popper-class="quality-feedback-tooltip">
                <div slot="content" class="tooltip-quality-detail">
                  <div><strong>Toxic Feedback:</strong> {{ toxicTone.count || 0 }} detected</div>
                  
                  <!-- Team breakdown -->
                  <div v-if="Object.keys(toxicByTeam).length > 0" class="mt-8">
                    <div class="flex-center mb-4">
                      <strong>Team Breakdown:</strong>
                      <span class="ml-auto fs-11 fc-6A6A6A">{{ Object.keys(toxicByTeam).length }} teams</span>
                    </div>
                    <div class="team-list-container">
                      <template v-for="(count, team, index) in sortedToxicByTeam">
                        <div v-if="showAllToxicTeams || index < 5" :key="team" class="team-breakdown-item">
                          <span class="team-rank">{{ index + 1 }}</span>
                          <span class="team-name" :class="blurMode ? 'blurelement' : ''">{{ team }}</span>
                          <span class="team-count">{{ count }}</span>
                        </div>
                      </template>
                    </div>
                    <div v-if="Object.keys(toxicByTeam).length > 5" class="mt-8">
                      <el-button 
                        type="text" 
                        size="mini" 
                        @click.stop="showAllToxicTeams = !showAllToxicTeams"
                        class="expand-button">
                        {{ showAllToxicTeams ? 'Show less' : `Show all ${Object.keys(toxicByTeam).length} teams` }}
                        <i :class="showAllToxicTeams ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      </el-button>
                    </div>
                  </div>
                  
                  <div v-if="(toxicTone.count || 0) > 0" class="mt-8">
                    <strong>Immediate Actions:</strong>
                    <br>• Review and address toxic feedback sources
                    <br>• Priority intervention for affected teams
                    <br>• Consider team coaching or support
                    <br>• Implement feedback moderation guidelines
                    <br>• Follow up with team managers
                  </div>
                  <div v-else class="mt-8 text-success">
                    ✓ Healthy communication environment maintained
                  </div>
                </div>
                <div class="emoji fs-14 fw-500 opa-09 ellipsis">
                  Toxic
                </div>
              </el-tooltip>
              <div class="percentage-bar ellipsis">
                <!-- No progress bar for absolute counts -->
              </div>
              <div class="percentage-number opa-09 fs-14">
                {{ toxicTone.count || 0 }}
              </div>
              <div class="percentage-change-number" v-if="toxicTone.percentageChange !== undefined" :class="(toxicTone.percentageChange || 0) < 0 ? 'up' : ((toxicTone.percentageChange || 0) > 0 ? 'down' : '')">
                {{ (toxicTone.percentageChange || 0) < 0 ? '' : '+' }}{{ toxicTone.percentageChange || 0 }}%
              </div>
            </div>
          </div>

        </div>

        
        <div>
          <div class="">
            <div class="flex-1">
              <span class="fs-18 fw-700">{{
                $t("insights.TALKING_POINTS")
              }}</span>
              <el-button type="text" class="txtButton" @click="showList">{{
                $t("insights.show_timeline")
              }}</el-button>
            </div>
          </div>


          <!-- dialog talking points -->
          <el-dialog :visible.sync="dialogVisible" @close="destroySentimentChart">
            <span slot="title">
              <div class="headerModalStyle flex-center">
                <div class="flex-1">
                  <span class="fs-20 fw-800 fc-2F2F2F">{{
                    $t("insights.TALKING_POINTS")
                  }}</span>
                </div>
                <div>
                  <el-tag style="margin: 0 8px 0 0 !important"
                    :class="['talking-points mt-2 mb-6 GreenBoxStyle', activeSentiments.positive ? 'legend-tag-active' : 'legend-tag-inactive']"
                    @click.native="toggleSentiment('positive')">{{
                    $t("insights.Positive") }}</el-tag>
                  <el-tag style="margin: 0 8px 0 0 !important"
                    :class="['talking-points mt-2 mb-6 GreyBoxStyle', activeSentiments.neutral ? 'legend-tag-active' : 'legend-tag-inactive']"
                    @click.native="toggleSentiment('neutral')">{{
                    $t("insights.Neutral") }}</el-tag>
                  <el-tag style="margin: 0 8px 0 0 !important"
                    :class="['talking-points mt-2 mb-6 RedBoxStyle', activeSentiments.negative ? 'legend-tag-active' : 'legend-tag-inactive']"
                    @click.native="toggleSentiment('negative')">{{
                    $t("insights.Critical") }}</el-tag>
                </div>
              </div>
            </span>
            <!-- Sentiment Chart with Viewport Navigator -->
            <div class="sentiment-chart-container" v-if="sentimentChartData && !loadingTimeLineTalkingPoints">
              <div class="sentiment-chart-wrapper" ref="sentimentChartWrapper">
                <canvas ref="sentimentChart" style="height: 130px; width: 100%;"></canvas>
                <div class="viewport-brush" ref="viewportBrush"
                  @mousedown.prevent="startViewportDrag($event)">
                  <div class="viewport-brush-handle left"></div>
                  <div class="viewport-brush-handle right"></div>
                </div>
              </div>
            </div>
            <div class="scroll-style thin-scrollbar" ref="scrollContainer">
              <div class="center mt-40 pt-40 mb-10 pb-10" v-if="loadingTimeLineTalkingPoints">
                <loading-even></loading-even>
              </div>
              <div class="mx-52 mb-30" v-for="(item, index) in talking_points_monthly" :key="index" :ref="'month-' + index">
                <div style="display: flex; justify-content: center" class="mb-8 mt-20">
                  <span class="txtMonthlyStyle">{{ setMonthly(index) }}</span>
                </div>
                <!-- Sentiment Breakdown Bar -->
                <div class="sentiment-breakdown" v-if="monthSentimentStats[index]">
                  <div class="sentiment-bar">
                    <div class="sentiment-bar-segment positive"
                      :style="{ width: (monthSentimentStats[index].total ? (monthSentimentStats[index].positive / monthSentimentStats[index].total * 100) : 0) + '%' }"></div>
                    <div class="sentiment-bar-segment neutral"
                      :style="{ width: (monthSentimentStats[index].total ? (monthSentimentStats[index].neutral / monthSentimentStats[index].total * 100) : 0) + '%' }"></div>
                    <div class="sentiment-bar-segment negative"
                      :style="{ width: (monthSentimentStats[index].total ? (monthSentimentStats[index].negative / monthSentimentStats[index].total * 100) : 0) + '%' }"></div>
                  </div>
                  <div class="sentiment-summary">
                    {{ monthSentimentStats[index].positive }} {{ $t("insights.Positive") }} &middot; {{ monthSentimentStats[index].neutral }} {{ $t("insights.Neutral") }} &middot; {{ monthSentimentStats[index].negative }} {{ $t("insights.Critical") }}
                  </div>
                </div>
                <!-- Lazy-rendered tag content -->
                <div v-if="visibleMonthKeys[index]" class="month-tags-content">
                  <div v-if="index < '2024-12'" class="flex" style="flex-wrap: wrap">
                    <div :class="['talkingPointsBox flex', { 'tag-enter': !animatedMonthKeys[index], 'tag-filtered-out': !activeSentiments[getSentimentType(_value, index)] }]" v-for="(_value, i_val) in item.slice(0, 20)" :key="_value._id"
                      :style="{ 'animation-delay': (i_val * 0.03) + 's' }">
                      <el-tooltip v-if="_value.short_answer.length > maxLength" effect="dark" placement="top">
                        <div class="tooltipStyle" slot="content">
                          {{ _value.short_answer }}
                        </div>
                        <el-tag :class="`talking-points ${_value.sentiment > 0
                          ? 'GreenBoxStyle'
                          : _value.sentiment < 0
                            ? 'RedBoxStyle'
                            : 'GreyBoxStyle'
                          }`" v-bind:style="'margin-bottom: 8px;' +
      $utils('fontStyle', _value.short_answer)
      ">{{ truncateText(_value.short_answer) }}</el-tag>
                      </el-tooltip>
                      <div v-else>
                        <el-tag :class="`talking-points ${_value.sentiment > 0
                          ? 'GreenBoxStyle'
                          : _value.sentiment < 0
                            ? 'RedBoxStyle'
                            : 'GreyBoxStyle'
                          }`" v-bind:style="'margin-bottom: 8px;' +
      $utils('fontStyle', _value.short_answer)
      ">{{ truncateText(_value.short_answer) }}</el-tag>
                      </div>
                    </div>
                  </div>
                  <div v-else class="flex" style="flex-wrap: wrap">
                    <div :class="['talkingPointsBox flex', { 'tag-enter': !animatedMonthKeys[index], 'tag-filtered-out': !activeSentiments[getSentimentType(_value, index)] }]" v-for="(_value,i_array) in item.slice(0, 20)" :key="index+'_'+i_array"
                      :style="{ 'animation-delay': (i_array * 0.03) + 's' }">
                      <el-tooltip effect="light" placement="top" class="custom-tooltip" :disabled="user.user_type == 'leadership'?true:false">
                        <div class="tooltipStyle" slot="content">
                          <span class="fs-13 fw-700">{{ _value.point.title }}</span>
                          <div v-if="_value.point && _value.point.specific_examples">
                            <div class="tooltip-items" v-for="(item_specific,index_specific) in _value.point.specific_examples" :key="index+'_'+index_specific">
                              <div class="tooltip-item">
                                <div class="oi-circle">
                                  <span class="fs-11">{{ index_specific+1 }}</span>
                                </div>
                                <p>
                                  {{ item_specific }}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <el-tag :class="`talking-points ${_value.sentiment == 'positive'
                          ? 'GreenBoxStyle'
                          : _value.sentiment == 'negative'
                            ? 'RedBoxStyle'
                            : 'GreyBoxStyle'
                          }`" v-bind:style="'margin-bottom: 8px;' +
                          $utils('fontStyle', _value.point.title)
                          ">{{ truncateText(_value.point.title) }}</el-tag>
                      </el-tooltip>
                    </div>
                  </div>
                </div>
                <div v-else class="month-tags-placeholder" :style="{ height: (monthContentHeights[index] || 160) + 'px' }"></div>
              </div>
            </div>
            <span slot="footer" class="dialog-footer" style="margin-right: 10px">
              <el-button @click="dialogVisible = false" class="buttonClose btn-gray-outline">{{ $t("insights.Close")
              }}</el-button>
            </span>
          </el-dialog>




          <div style="margin-top: 8px; max-height: 310px; flex-wrap: wrap" class="thin-scrollbar flex">
            <div class="talkingPointsBox" v-for="(item,index) in talking_points_daily.slice(0, 20)" :key="index">
              <el-tooltip effect="light" placement="top" class="custom-tooltip" :disabled="user.user_type == 'leadership'?true:false">
                <div class="tooltipStyle" slot="content">
                  <span class="fs-13 fw-700">{{ item.point.title }}</span>
                  <div v-if="item.point && item.point.specific_examples">
                    <div class="tooltip-items" v-for="(item_specific,index_specific) in item.point.specific_examples" :key="index+'_'+index_specific">
                      <div class="tooltip-item">
                        <div class="oi-circle">
                          <span class="fs-11">{{ index_specific+1 }}</span>
                        </div>
                        <p>
                          {{ item_specific }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <el-tag :class="`talking-points ${item.sentiment == 'positive'
                  ? 'GreenBoxStyle'
                  : item.sentiment == 'negative'
                    ? 'RedBoxStyle'
                    : 'GreyBoxStyle'
                  }`" v-bind:style="'margin-bottom: 8px;' +
                  $utils('fontStyle', item.point.title)
                  ">{{ truncateText(item.point.title) }}</el-tag>
              </el-tooltip>


            </div>  
          </div>






        </div>

        
      </div>


      <div class="flex-center mt-40">
        <div class="flex-1">
          <span class="fs-18 fw-700">{{
            $t("insights.ENGAGEMENT_BREAKDOWN_BY_TEAM")
          }}</span>
        </div>
        <div style="padding-right: 12px">
          <span class="fc-000000 fs-14">{{ $t("insights.Sort_by") }}</span>
        </div>
        <div class="showOption">
          <el-menu class="el-menu-demo menuOptionStyle" mode="horizontal" style="width: 220px">
            <el-submenu index="0" style="width: 220px">
              <template slot="title">
                <span class="fc-4A4A4A fs-16 ml-16" style="width: 160px; display: inline-block" id="sortby">{{
                  $t("insights.Lowest_Success_Score") }}</span>
              </template>
              <el-menu-item index="1" class="ml-16 fs-16" @click="sortBy('lowest')" style="width: 220px">{{
                $t("insights.Lowest_Success_Score") }}</el-menu-item>
              <el-menu-item index="2" class="ml-16 fs-16" @click="sortBy('highest')" style="width: 220px">{{
                $t("insights.Highest_Success_Score") }}</el-menu-item>
            </el-submenu>
          </el-menu>
        </div>
      </div>
      <div class="divTable">
        <el-table :data="questions" :default-sort="{ prop: 'name', order: 'ascending' }" style="width: 100%"
          empty-text="Loading..." :key="questionsTableKey">
          <el-table-column :label="$t('insights.Dimension')" width="90" align="center">
            <template slot-scope="scope">
              <div class="flex-center">
                <img v-if="scope.row.dimpic" v-lazy="getImgUrlDim(scope.row.dimpic)"
                  class="image-skeleton image-profile-60" />
                <div v-else class="divCurclePic"></div>
              </div>
            </template>
          </el-table-column>
          <el-table-column>
            <template slot-scope="scope">
              <div class="flex-column" style="cursor: pointer" @click="DimensionClicked(scope.row)">
                <span class="fc-4A4A4A fs-16 fw-700">{{ scope.row.name }}</span>
                <span class="txtDetailTable">{{
                  questions_name[scope.row.quest_id]
                }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('insights.Status_no_teams')" width="190" align="center">
            <template slot-scope="scope">
              <div class="flex-center">
                <div class="divCurcleStatus green">
                  <span class="txtStatus">{{
                    $utils("addCommasInNumber", scope.row.status.green)
                  }}</span>
                </div>
                <div class="divCurcleStatus yellow">
                  <span class="txtStatus">{{
                    $utils("addCommasInNumber", scope.row.status.yellow)
                  }}</span>
                </div>
                <div class="divCurcleStatus red">
                  <span class="txtStatus">{{
                    $utils("addCommasInNumber", scope.row.status.red)
                  }}</span>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop :label="$t('insights.Success_Score')" width="180" align="right">
            <template slot-scope="scope">
              <div class="success-score-cell">
                <div class="score-and-change">
                  <span class="fc-2B2B2B fs-16">{{ scope.row.success }}</span>
                  <span v-if="scope.row.compare > 0" class="txtPercent green">+{{ scope.row.compare }}%</span>
                  <span v-else-if="scope.row.compare < 0" class="txtPercent red">{{ scope.row.compare }}%</span>
                  <span v-else-if="scope.row.compare == 0" class="txtPercent">+0%</span>
                </div>
                <div class="mini-trend" v-if="getMiniTrend(scope.row.quest_id).length >= 2">
                  <svg :width="miniTrendWidth" :height="miniTrendHeight" viewBox="0 0 40 16">
                    <polyline
                      :points="computeSparkline(getMiniTrend(scope.row.quest_id))"
                      fill="none"
                      :stroke="sparkColor(getMiniTrend(scope.row.quest_id))"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <el-dialog :visible.sync="dialogDimensionVisible">
        <span slot="title">
          <div class="headerModalStyle flex-center">
            <div class="flex-1">
              <span class="fs-20 fw-800 fc-2F2F2F">{{
                $t("insights.DIMENSION_DETAILS")
              }}</span>
            </div>
          </div>
        </span>
        <div class="scroll-style scrollingStyle2 thin-scrollbar">
          <div class="mt-2 mx-20 mb-32">
            <div class="flex-center-1">
              <img v-if="dimensionData.dimpic" :src="getImgUrlDim(dimensionData.dimpic)"
                class="image-skeleton image-profile-60" />
              <div v-else class="divCurclePic"></div>
              <div class="ml-21 mt-10">
                <div>
                  <span class="fc-000000 fs-18 fw-700">{{
                    dimensionData.name
                  }}</span>
                </div>
                <div class="mt-4">
                  <span class="txtDimensionDesc">{{
                    questions_name[dimensionData.quest_id]
                  }}</span>
                </div>
              </div>
            </div>
            <div class="flex mt-15" style="height: 300px">
              <canvas ref="chartTalkingPoints" class="chartStyle"></canvas>
            </div>
            <div class="flex-1 mt-24 mb-16">
              <span class="fs-18 fc-000000 fw-700">
                {{ $t("insights.TALKING_POINTS") }}
              </span>
            </div>
            <div style="margin-top: 8px; max-height: 164px; flex-wrap: wrap" class="flex">
              <div class="talkingPointsBox" v-for="(item,index) in talking_points_30d.slice(0, 20)" :key="index">
                <el-tooltip effect="light" placement="top" class="custom-tooltip">
                  <div class="tooltipStyle" slot="content">
                    <span class="fs-13 fw-700">{{ item.point.title }}</span>
                    <div v-if="item.point && item.point.specific_examples">
                      <div class="tooltip-items" v-for="(item_specific,index_specific) in item.point.specific_examples" :key="index+'_'+index_specific">
                        <div class="tooltip-item">
                          <div class="oi-circle">
                            <span class="fs-11">{{ index_specific+1 }}</span>
                          </div>
                          <p>
                            {{ item_specific }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <el-tag :class="`talking-points ${item.sentiment == 'positive'
                    ? 'GreenBoxStyle'
                    : item.sentiment == 'negative'
                      ? 'RedBoxStyle'
                      : 'GreyBoxStyle'
                    }`" v-bind:style="'margin-bottom: 8px;' +
                      $utils('fontStyle', item.point.title)
                      ">{{ truncateText(item.point.title) }}</el-tag>
                </el-tooltip>
              </div>
            </div>
          </div>
        </div>
        <span slot="footer" class="dialog-footer" style="margin-right: 10px">
          <el-button @click="dialogDimensionVisible = false" class="buttonClose btn-gray-outline">{{ $t("insights.Close")
          }}</el-button>
        </span>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
Chart.defaults.font.family = "Lato";
import ChartDataLabels from "chartjs-plugin-datalabels";
import format from "date-fns/format";
import { Result } from "element-ui";

export default {
  name: "dashboard",
  components: {
    loadingEven: require("../even/loading").default,
  },
  async created() {
    // Initialize dashboard data if user is already available
    // Otherwise, the watcher will handle it when user loads
    if (this.user) {
      await this.initializeDashboard();
    }
  },
  mounted() {
    // Listen for language changes
    this.$root.$on('languageChanged', this.handleLanguageChange);
  },
  props: {
    tabActive: String,
  },
  computed: {
    user() {
      return this.$store.getters["authen/user"];
    },
    dimension() {
      return this.$store.getters["insights/dimension"];
    },
    activeuser() {
      return this.$store.getters["dashboard/activeuser"];
    },
    happiness() {
      return this.$store.getters["dashboard/happiness"];
    },
    feedback() {
      return this.$store.getters["dashboard/feedback"];
    },
    dateshow() {
      return this.$store.getters["dashboard/dateshow"];
    },
    questions() {
      return this.$store.getters["dashboard/questions"];
    },
    questions_name() {
      return this.$store.getters["dashboard/questions_name"];
    },
    talking_points_daily() {
      return this.$store.getters["dashboard/talking_points_daily"];
    },
    talking_points_monthly() {
      this.talkingPointsTimeLineCallAPI = ++this.talkingPointsTimeLineCallAPI;
      return this.$store.getters["dashboard/talking_points_monthly"];
    },
    talking_points_30d() {
      return this.$store.getters["dashboard/talking_points_30d"];
    },
    datachart() {
      return this.$store.getters["dashboard/datachart"];
    },
    databarchart() {
      return this.$store.getters["dashboard/databarchart"];
    },
    databarchartLoading() {
      return this.$store.getters["dashboard/datachartloadingStatus"];
    },
    databarchartEngagementScore() {
      return this.$store.getters["dashboard/databarchartEngagementScore"];
    },
    EngagementScoreLoading() {
      return this.$store.getters["dashboard/EngagementScoreLoadingStatus"];
    },
    datakeygroup() {
      return this.$store.getters["dashboard/datakeygroup"];
    },
    datakeyGroupLoading() {
      return this.$store.getters["dashboard/datakeygrouploadingStatus"];
    },
    opt_month_name() {
      return this.$store.getters["dashboard/opt_month_name"];
    },
    loadingTimeLineTalkingPoints() {
      if (
        Object.keys(this.talking_points_monthly).length === 0 &&
        this.talkingPointsTimeLineCallAPI < 2
      ) {
        return true;
      } else {
        return false;
      }
    },
    sentimentChartData() {
      const monthly = this.talking_points_monthly;
      if (!monthly || Object.keys(monthly).length === 0) return null;
      const sortedKeys = Object.keys(monthly).sort();
      const labels = [];
      const positive = [];
      const neutral = [];
      const negative = [];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      sortedKeys.forEach(key => {
        const split = key.split('-');
        const monthIdx = parseInt(split[1]) - 1;
        const year = split[0].slice(2);
        const monthLabel = (monthIdx >= 0 && monthIdx < 12) ? monthNames[monthIdx] : '?';
        labels.push(monthLabel + ' \'' + year);
        let pos = 0, neu = 0, neg = 0;
        const items = monthly[key] || [];
        items.forEach(item => {
          const type = this.getSentimentType(item, key);
          if (type === 'positive') pos++;
          else if (type === 'negative') neg++;
          else neu++;
        });
        positive.push(pos);
        neutral.push(neu);
        negative.push(neg);
      });
      return { labels, positive, neutral, negative, keys: sortedKeys };
    },
    monthSentimentStats() {
      const monthly = this.talking_points_monthly;
      if (!monthly) return {};
      const stats = {};
      Object.keys(monthly).forEach(key => {
        let pos = 0, neu = 0, neg = 0;
        const items = monthly[key] || [];
        items.forEach(item => {
          const type = this.getSentimentType(item, key);
          if (type === 'positive') pos++;
          else if (type === 'negative') neg++;
          else neu++;
        });
        stats[key] = { positive: pos, neutral: neu, negative: neg, total: pos + neu + neg };
      });
      return stats;
    },
    newLabels() {
      return this.$store.getters["dashboard/newLabels"];
    },
    toneData() {
      return this.$store.getters["dashboard/toneAnalyse"];
    },
    toneLoadingData() {
      return this.$store.getters["dashboard/toneloadingStatus"];
    },
    gibbrishTone() {
      return this.$store.getters["dashboard/gibbrishTone"];
    },
    toxicTone() {
      return this.$store.getters["dashboard/toxicTone"];
    },
    gibbrishByTeam() {
      return this.$store.getters["dashboard/gibbrishByTeam"];
    },
    toxicByTeam() {
      return this.$store.getters["dashboard/toxicByTeam"];
    },
    sortedGibbrishByTeam() {
      const teams = this.gibbrishByTeam;
      // Convert object to array and sort by count (descending)
      return Object.entries(teams)
        .sort(([, a], [, b]) => b - a)
        .reduce((acc, [team, count]) => {
          acc[team] = count;
          return acc;
        }, {});
    },
    sortedToxicByTeam() {
      const teams = this.toxicByTeam;
      // Convert object to array and sort by count (descending)
      return Object.entries(teams)
        .sort(([, a], [, b]) => b - a)
        .reduce((acc, [team, count]) => {
          acc[team] = count;
          return acc;
        }, {});
    },
    problemFeedbackDetails() {
      return this.$store.getters["dashboard/problemFeedbackDetails"];
    },
    maximumUsers() {
      return this.$store.getters["dashboard/maximumUsers"];
    },
    past12Hour() {
      return this.$store.getters["dashboard/past12Hour"];
    },
    activeRecord() {
      return this.$store.getters["dashboard/activeRecord"];
    },
    questionOpenEnd() {
      return this.$store.getters["dashboard/questionOpenEnd"];
    },
    activeOverview() {
      return this.$store.getters["dashboard/managerData"];
    },
    engagementpeople() {
      return this.$store.getters["dashboard/engagementpeople"];
    },
    enps() {
      return this.$store.getters["dashboard/enps"];
    },
    adoption() {
      return this.$store.getters["dashboard/adoption"];
    },
    monthlyActiveUser() {
      return this.$store.getters["dashboard/monthlyActiveUser"];
    },
    weeklyActiveUser() {
      return this.$store.getters["dashboard/weeklyActiveUser"];
    },
    miniTrends() {
      return this.$store.getters["dashboard/miniTrends"] || {};
    },
    enpsLoading() {
      return this.$store.getters["dashboard/enpsLoading"];
    },
    impactRatingLoading() {
      return this.$store.getters["dashboard/impactRatingLoading"];
    },
    happinessLoading() {
      return this.$store.getters["dashboard/happinessLoading"];
    },
    feedbackLoading() {
      return this.$store.getters["dashboard/feedbackLoading"];
    },

    activityLoadingStatus() {
      return this.$store.getters["dashboard/activityLoadingStatus"];
    },
    benchmarkBreak() {
      return this.$store.getters["dashboard/benchmarkBreak"];
    },
    blurMode() {
      return this.$store.getters["demomode/blurMode"];
    },
    activeIsGreater() {
      return (
        parseInt(this.activeOverview.active) >
        (parseInt(this.activeOverview.passive) + parseInt(this.activeOverview.inactive))
      );
    },
    // Focus & Goal Alignment computed properties
    focusAlignmentLoading() {
      return this.$store.getters["focus/loadingHistorical"] || 
             this.$store.getters["focus/isLoadingHistoricalTeam"]('All Teams', true);
    },
    focusCoverageLoading() {
      return this.$store.getters["focus/loading"];
    },
    focusAlignmentData() {
      return this.$store.getters["focus/getHistoricalSummaryStats"]('All Teams', true);
    },
    focusSummaryStats() {
      return this.$store.getters["focus/summaryStats"];
    },
    goalAlignmentRate() {
      if (this.focusAlignmentData) {
        return this.focusAlignmentData.alignmentRate;
      }
      return null;
    },
    isGoalAlignmentApplicable() {
      return this.goalAlignmentRate !== null;
    },
    goalAlignmentHealthColor() {
      const rate = this.goalAlignmentRate;
      if (rate === null) return '#909399'; // Gray for N/A
      if (rate < 33) return '#F44336'; // Red
      if (rate < 66) return '#FBC02D'; // Yellow
      return '#35D077'; // Green
    },
    focusCoverageRate() {
      const stats = this.focusSummaryStats;
      if (!stats || stats.totalPeople === 0) return 0;
      return Math.round((stats.withFocus / stats.totalPeople) * 100);
    },
    focusCoverageHealthColor() {
      const rate = this.focusCoverageRate;
      if (rate < 33) return '#F44336'; // Red
      if (rate < 66) return '#FBC02D'; // Yellow
      return '#35D077'; // Green
    },
    // Weekly change computed properties
    focusCoverageWeekChange() {
      return this.$store.getters["focus/getFocusCoverageWeekChange"](this.teamHight);
    },
    goalAlignmentWeekChange() {
      return this.$store.getters["focus/getGoalAlignmentWeekChange"](this.teamHight);
    },
    loadingWeeklySummary() {
      return this.$store.getters["focus/isLoadingWeeklySummary"](this.teamHight);
    },
    // Multi-week trend data for retrieving latest weekly values
    focusCoverageTrendData() {
      return this.$store.getters["focus/getWeeklyTrendData"]('All Teams', 'focusCoverage', 2);
    },
    goalAlignmentTrendData() {
      return this.$store.getters["focus/getWeeklyTrendData"]('All Teams', 'alignedItemsPercentage', 2);
    },
    // Learning Journeys computed properties
    learningOverview() {
      return this.$store.getters["learningJourneys/overview"] || { active: 0, completed: 0, upskilling: 0, learning_time: 0 };
    },
    learningLoading() {
      return this.$store.getters["learningJourneys/loading"];
    },
  },
  beforeDestroy() {
    this.$store.dispatch("dashboard/closePast12Hours", this.user);
    // this.$store.dispatch("dashboard/closeAdoption", this.user);
    // Clean up event listener
    this.$root.$off('languageChanged', this.handleLanguageChange);
    this.destroySentimentChart();
  },
  data() {
    return {
      dialogVisible: false,
      dialogDimensionVisible: false,
      dimensionData: [
        {
          dimpic: "",
          name: "",
          quest_id: "",
        },
      ],
      talkingg_point_bar_chart: "",
      engagement_score_bar_chart: "",
      dashboard_bar_chart: "",
      real_time_chart: "",
      maxLength: 60,
      questionsTableKey: 0, // Key to force table re-render when language changes
      //benchmarkBreak: "eNPS",
      chartOptions: {
        // responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: "none",
        },
        title: {
          display: false,
        },
        hover: {
          animationDuration: 0,
        },
        scales: {
          yAxes: [
            {
              stacked: false,
              ticks: {
                display: false,
                beginAtZero: true,
                stepSize: 20,
              },
              gridLines: {
                display: false,
                drawBorder: false,
              },
            },
          ],
          xAxes: [
            {
              gridLines: {
                display: false,
                fontColor: "black",
              },
            },
          ],
        },
        layout: {
          padding: {
            top: 18,
          },
        },
      },
      talkingPointsTimeLineCallAPI: 0,
      engagementImpactLoadingStatus: true,
      weeklyLoadingStatus: true,
      miniTrendWidth: 40,
      miniTrendHeight: 16,
      showAllGibberishTeams: false,
      showAllToxicTeams: false,
      teamHight: '',
      sentimentChartInstance: null,
      sentimentChartUnwatch: null,
      activeSentiments: { positive: true, neutral: true, negative: true },
      viewportDragging: false,
      viewportDragStartX: 0,
      viewportScrollStartLeft: 0,
      currentViewportIndex: -1,
      boundHandleViewportDrag: null,
      boundStopViewportDrag: null,
      boundOnContentScroll: null,
      visibleMonthKeys: {},
      animatedMonthKeys: {},
      monthObserver: null,
      monthContentHeights: {},
      _scrollRafId: null,
    };
  },
  watch: {
    // Watch for user to become available
    user: {
      handler(newUser, oldUser) {
        // Initialize dashboard when user first becomes available
        if (newUser && !oldUser) {
          this.initializeDashboard();
        }
      },
      immediate: false
    },
    // Watch miniTrends to trigger re-render when individual trends load
    miniTrends: {
      handler() {
        // This will trigger Vue to re-evaluate the template when miniTrends updates
      },
      deep: true
    },
    datachart() {
      if (this.talkingg_point_bar_chart)
        this.talkingg_point_bar_chart.destroy();
      this.randerChart();
    },
    databarchart() {
      if (this.dashboard_bar_chart) this.dashboard_bar_chart.destroy();
      this.randerBarChart();
    },
    databarchartEngagementScore() {
      if (this.engagement_score_bar_chart)
        this.engagement_score_bar_chart.destroy();
      this.renderChartEngagementScoreCompany();
      this.renderChartEngagementScoreManager();
      this.renderChartEngagementScoreLeader();
    },

    activeRecord() {
      if (this.real_time_chart) this.real_time_chart.destroy();
      this.randerLineChart();
    },
    enpsLoading() {
      this.setEngagementImpactLoading();
    },

    impactRatingLoading() {
      this.setEngagementImpactLoading();
    },
    happinessLoading() {
      this.setWeeklyLoadingStatus();
    },
    feedbackLoading() {
      this.setWeeklyLoadingStatus();
    },
    tabActive(val) {
      if (this.talkingg_point_bar_chart) {
        this.talkingg_point_bar_chart.destroy();
      }
      if (this.dashboard_bar_chart) {
        this.dashboard_bar_chart.destroy();
      }
      if (this.real_time_chart) {
        this.real_time_chart.destroy();
      }

      if (val == "dashboard") {
        this.randerChart();
        this.randerBarChart();
        this.randerLineChart();
      }
    },
    enps: {
      handler(newVal) {
        // Watch for eNPS changes
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    async initializeDashboard() {
      if (!this.user) return;

      const result = await this.$store.dispatch("dashboard/getMemberAndTeam", this.user);

      this.$store.dispatch("dashboard/getManager", result.users);
      this.$store.dispatch("dashboard/getAdoption", result.users);
      this.$store.dispatch("dashboard/getImpactRating", result.users);
      this.$store.dispatch("dashboard/getEnps", {
        user: this.user,
        teams: result.teams,
      });
      this.teamHight = result.teamHight;
      this.$store.dispatch("dashboard/getPast12Hours", this.user);
      this.$store.dispatch("dashboard/getGlobalVariable", this.$global_variable);
      this.$store.dispatch("dashboard/setWeekOfYear");
      this.$store.dispatch("dashboard/getActivityThisWeek", this.user);
      this.$store.dispatch("dashboard/getHappinessThisWeek", this.user);
      this.$store.dispatch("dashboard/getFeedbackThisWeek", this.user);
      // Load questions data and names first
      await Promise.all([
        this.$store.dispatch("dashboard/getQuestionData", this.user),
        this.$store.dispatch("dashboard/getQuestionsName")
      ]);

      // Load mini trends non-blocking in the background
      this.$store.dispatch("dashboard/refreshMiniTrends");

      // Continue loading other data immediately
      this.$store.dispatch("dashboard/getTalkPointsDaily", this.user);
      this.$store.dispatch("dashboard/getDataJobTenure", this.user);
      this.$store.dispatch("dashboard/getDataKeyGroup", this.user);
      this.$store.dispatch("dashboard/getDataToneAnalyze", this.user);
      this.$store.dispatch("dashboard/getQuestionOpenEnd", this.user);
      this.$store.dispatch("dashboard/getDataEngagementScore", this.user);
      this.$store.dispatch("dashboard/getComEncryp", this.user);
      
      // Load focus & goal alignment data
      this.$store.dispatch("focus/loadHistoricalFocusDataForTeam", {
        teamId: 'All Teams',
        includeChildren: true,
      });
      // Load focus data for summaryStats (no team filter = all teams)
      this.$store.dispatch("focus/loadFocusData");
      // Load weekly summary for week-over-week changes
      this.$store.dispatch("focus/loadWeeklySummaryForTeam", {
        teamId: result.teamHight,
      });
      // Load learning journeys data
      this.$store.dispatch("learningJourneys/getLearningJourneys");
    },
    isNegativeTone(toneId) {
      const negativeTones = [
        'anger', 'fear', 'sadness', 'sad', 'frustrated', 'impolite',
        'aggressive', 'passive-aggressive', 'dismissive', 'gossiping',
        'overly-critical', 'gibberish', 'toxic', 'disrespectful', 'condescending'
      ];
      return negativeTones.includes(toneId);
    },
    async handleLanguageChange(langCode) {
      // Refresh question names when language changes
      await this.$store.dispatch("dashboard/getQuestionsName");
      // Force table re-render by changing the key
      this.questionsTableKey += 1;
    },
    randerLineChart() {
      var chart = this.$refs.pastLinechart;
      if (chart) {
        const lineChartData = {
          labels: this.activeRecord.time, //["04-05 AM","04-05 AM","04-05 AM","","","","","","","","","04-05 AM"],
          datasets: [
            {
              backgroundColor: "#7E3FC700",
              borderColor: "#35D077",
              pointHoverBackgroundColor: "#35D077",
              pointBackgroundColor: "#35D077",
              pointRadius: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 4],
              lineTension: 0,
              data: [20, 21, 50, 80, 25, 41, 20, 4, 0, 0, 4, 12], // TODO: Add data to data
              data: this.activeRecord.data, // [20, 21, 50, 80, 25, 41, 20, 4, 0, 0, 4 , 12], // TODO: Add data to data
              pointHitRadius: 6,
              pointHoverRadius: 6,
              borderWidth: 2,
              // data: this.activeRecord.data, // [20, 21, 50, 80, 25, 41, 20, 4, 0, 0, 4 , 12], // TODO: Add data to data
            },
          ],
        };
        var ctx = chart.getContext("2d");
        const options = JSON.parse(JSON.stringify(this.variable.chartOptioins));
        options.plugins = {
          legend: {
            display: false,
          },
          tooltip: {
            mode: "index",
            position: "nearest",
            displayColors: false,
            callbacks: {
              label: function (tooltipItems) {
                const label = tooltipItems.dataset.label;
                const value = tooltipItems.formattedValue;
                return `Active: ${value}`;
              },
            },
          },
        };
        options.scales.y.display = false;
        options.scales.x.display = false;
        this.real_time_chart = new Chart(ctx, {
          type: "line",
          data: lineChartData,
          options: options,
        });
      }
    },
    randerBarChart() {
      var chart = this.$refs.barchart;
      // Guard clause: ensure databarchart and its labels exist before proceeding
      if (!this.databarchart || !this.databarchart.labels) {
        return;
      }
      let chartlabels = [];
      this.databarchart.labels.forEach(function (item, index) {
        chartlabels.push(item.split("yr").join(" yr"));
      });
      if (chart) {
        const barChartData = {
          labels: chartlabels,
          datasets: [
            {
              label: "Weekly Active",
              backgroundColor: "#7E3FC7",
              borderColor: "#6C30CC",
              borderWidth: 1,
              
              data: this.databarchart.active,
              people_count: this.databarchart.people_count,
              datalabels: {
                display: false,
              },
            },
            {
              label: "Happiness",
              backgroundColor: "#66BB6A",
              borderColor: "#66BB6A",
              borderWidth: 1,
              data: this.databarchart.happy,
              datalabels: {
                display: false,
              },
            },
            {
              label: "Risk",
              backgroundColor: "#F44336",
              borderColor: "#F44336",
              borderWidth: 1,
              data: this.databarchart.worse,
              datalabels: {
                color: "#F44336",
                anchor: "end",
                align: "end",
                clamp: true,
                offset: 0,
              },
            },
            {
              label: "Open Feedback Rate",
              backgroundColor: "#999",
              borderColor: "#999",
              borderWidth: 1,
              data: this.databarchart.feedback,
              datalabels: {
                display: false,
              },
            },
          ],
        };
        const options = JSON.parse(JSON.stringify(this.variable.chartOptioins));
        options.plugins.datalabels = {
          formatter: function (value, context) {
            return value + "%";
          },
          font: {
            weight: "bold",
            size: 10,
          },
        };
        options.plugins.tooltip = {
          mode: "index",
          position: "nearest",
          callbacks: {
            label: function (tooltipItems) {
              const label = tooltipItems.dataset.label;
              const value = tooltipItems.formattedValue;
              if (tooltipItems.datasetIndex == 0) {
                const people =
                  tooltipItems.dataset.people_count[tooltipItems.dataIndex];
                return `${label}: ${value}% (${people} people)`;
              }
              return `${label}: ${value}%`;
            },
          },
        };
        const ctx = chart.getContext("2d");
        this.dashboard_bar_chart = new Chart(ctx, {
          type: "bar",
          plugins: [ChartDataLabels],
          data: barChartData,
          options: options,
        });
      }
    },
    randerChart() {
      var chart = this.$refs.chartTalkingPoints;
      if (chart) {
        var ctx = chart.getContext("2d");
        const options = JSON.parse(JSON.stringify(this.variable.chartOptioins));
        options.responsive = true;
        options.maintainAspectRatio = false;
        // const data = {
        //   datasets: [
        //     {
        //       label: "My First Dataset",
        //       data: [65, 59, 80, 81, 56, 55, 40],
        //       fill: false,
        //       borderColor: "rgb(75, 192, 192)",
        //       tension: 0.1,
        //     },
        //   ],
        // };
        this.talkingg_point_bar_chart = new Chart(ctx, {
          type: "line",
          data: {
            labels: this.opt_month_name,
            datasets: [
              {
                label: "SUCCESS SCORE",
                data: this.datachart.success,
                newLabels: this.newLabels,
                backgroundColor: "rgba(226, 215, 244, 1)",
                fill: true,
                borderColor: "rgba(108, 48, 204, 1)",
                borderWidth: 1,
              },
            ],
          },
          options: {
            maintainAspectRatio: false,
            scales: {
              y: {
                max: 100,
                min: 0,
                ticks: {
                  stepSize: 50,
                  callback: function (value) {
                    return value + "%";
                  },
                },
              },
              x: {
                ticks: {
                  autoSkip: false,
                },
              },
            },
            plugins: {
              datalabels: {
                formatter: function (value, context) {
                  return value + "%";
                },
                font: {
                  weight: "bold",
                  size: 10,
                },
              },
              tooltip: {
                mode: "index",
                position: "nearest",
                callbacks: {
                  title: function (tooltipItems) {
                    return tooltipItems[0].dataset.newLabels[
                      tooltipItems[0].dataIndex
                    ];
                  },
                  label: function (tooltipItems) {
                    const label = tooltipItems.dataset.label;
                    const value = tooltipItems.formattedValue;
                    return `${label}: ${value}%`;
                  },
                },
              },
            },
            elements: {
              line: {
                tension: 0,
              },
            },
            responsive: true,
          },
        });
      }
    },
    renderChartEngagementScoreCompany() {
      var chart = this.$refs.chartEngagementScoreCompany;
      if (chart) {
        var ctx = chart.getContext("2d");
        const options = JSON.parse(JSON.stringify(this.variable.chartOptioins));

        options.responsive = true;
        options.maintainAspectRatio = false;
        let opt_month_name = this.databarchartEngagementScore['labels']
        let maxScore = this.databarchartEngagementScore['maxScore']
        let yMax = maxScore < 50 ? 50 : (maxScore < 75 ? 75 : 100);
        this.engagement_score_bar_chart = new Chart(ctx, {
          type: "line",
          data: {
            labels: opt_month_name,
            datasets: [
              {
                // label: "Jun 12",
                data: this.databarchartEngagementScore['company'],
                newLabels: this.databarchartEngagementScore['newLabels'],
                backgroundColor: "#32C2D5",
                fill: true,
                borderColor: "#32C2D5",
                borderWidth: 1,
                pointRadius: 0
              },
            ],
          },
          options: {
            interaction: {
              intersect: false,
              mode: 'nearest'
            },
            maintainAspectRatio: false,
            scales: {
              y: {
                display: false,
                max: yMax,
                min: 0,
                ticks: {
                  stepSize: 50,
                  callback: function (value) {
                    return value;
                  },
                },
                grid: {
                  display: false,
                },
              },
              x: {
                ticks: {
                  autoSkip: false,
                  maxRotation: 0,
                  minRotation: 0,
                  font: {
                    color: 'black',
                    size: 12,
                    weight: 'bold',
                    family: 'Lato'
                  },
                },
                grid: {
                  display: false,
                },
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              datalabels: {
                formatter: function (value, context) {
                  return value;
                },
                font: {
                  weight: "bold",
                  size: 10,
                },
              },
              tooltip: {
                mode: "index",
                position: "nearest",
                callbacks: {
                  title: (tooltipItems) => {
                    return this.databarchartEngagementScore['newLabels'][tooltipItems[0].dataIndex];
                  },
                  label: function (tooltipItems) {
                    const value = tooltipItems.formattedValue;
                    return ` ${value}`;
                  },
                },
                titleFont: {
                  weight: 'bold',
                },
              },
            },
            elements: {
              line: {
                tension: 0,
              },
            },
            responsive: true,
          },
        });
      }
    },
    renderChartEngagementScoreManager() {
      var chart = this.$refs.chartEngagementScoreManager;
      if (chart) {
        var ctx = chart.getContext("2d");
        const options = JSON.parse(JSON.stringify(this.variable.chartOptioins));

        options.responsive = true;
        options.maintainAspectRatio = false;
        let opt_month_name = this.databarchartEngagementScore['labels']
        let maxScore = this.databarchartEngagementScore['maxScore']
        let yMax = maxScore < 50 ? 50 : (maxScore < 75 ? 75 : 100);
        this.engagement_score_bar_chart = new Chart(ctx, {
          type: "line",
          data: {
            labels: opt_month_name,
            datasets: [
              {
                // label: "Jun 12",
                data: this.databarchartEngagementScore['manager'],
                newLabels: this.databarchartEngagementScore['newLabels'],
                backgroundColor: "#E45006",
                fill: true,
                borderColor: "#E45006",
                borderWidth: 1,
                pointRadius: 0
              },
            ],
          },
          options: {
            interaction: {
              intersect: false,
              mode: 'nearest'
            },
            maintainAspectRatio: false,
            scales: {
              y: {
                display: false,
                max: yMax,
                min: 0,
                ticks: {
                  stepSize: 50,
                  callback: function (value) {
                    return value;
                  },
                },
                grid: {
                  display: false,
                },
              },
              x: {
                ticks: {
                  autoSkip: false,
                  maxRotation: 0,
                  minRotation: 0,
                  font: {
                    color: 'black',
                    size: 12,
                    weight: 'bold',
                    family: 'Lato'
                  },
                },
                grid: {
                  display: false,
                },
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              datalabels: {
                formatter: function (value, context) {
                  return value;
                },
                font: {
                  weight: "bold",
                  size: 10,
                },
              },
              tooltip: {
                mode: "index",
                position: "nearest",
                callbacks: {
                  title: (tooltipItems) => {
                    return this.databarchartEngagementScore['newLabels'][tooltipItems[0].dataIndex];
                  },
                  label: function (tooltipItems) {
                    const value = tooltipItems.formattedValue;
                    return ` ${value}`;
                  },
                },
                titleFont: {
                  weight: 'bold',
                },
              },
            },
            elements: {
              line: {
                tension: 0,
              },
            },
            responsive: true,
          },
        });
      }
    },
    renderChartEngagementScoreLeader() {
      var chart = this.$refs.chartEngagementScoreLeader;
      if (chart) {
        var ctx = chart.getContext("2d");
        const options = JSON.parse(JSON.stringify(this.variable.chartOptioins));

        options.responsive = true;
        options.maintainAspectRatio = false;
        let opt_month_name = this.databarchartEngagementScore['labels']
        let maxScore = this.databarchartEngagementScore['maxScore']
        let yMax = maxScore < 50 ? 50 : (maxScore < 75 ? 75 : 100);
        this.engagement_score_bar_chart = new Chart(ctx, {
          type: "line",
          data: {
            labels: opt_month_name,
            datasets: [
              {
                // label: "Jun 12",
                data: this.databarchartEngagementScore['leader'],
                newLabels: this.databarchartEngagementScore['newLabels'],
                backgroundColor: "#E048DA",
                fill: true,
                borderColor: "#E048DA",
                borderWidth: 1,
                pointRadius: 0
              },
            ],
          },
          options: {
            interaction: {
              intersect: false,
              mode: 'nearest'
            },
            maintainAspectRatio: false,
            scales: {
              y: {
                display: false,
                max: yMax,
                min: 0,
                ticks: {
                  stepSize: 50,
                  callback: function (value) {
                    return value;
                  },
                },
                grid: {
                  display: false,
                },
              },
              x: {
                ticks: {
                  autoSkip: false,
                  maxRotation: 0,
                  minRotation: 0,
                  font: {
                    color: 'black',
                    size: 12,
                    weight: 'bold',
                    family: 'Lato'
                  },
                },
                grid: {
                  display: false,
                },
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              datalabels: {
                formatter: function (value, context) {
                  return value;
                },
                font: {
                  weight: "bold",
                  size: 10,
                },
              },
              tooltip: {
                mode: "index",
                position: "nearest",
                callbacks: {
                  title: (tooltipItems) => {
                    return this.databarchartEngagementScore['newLabels'][tooltipItems[0].dataIndex];
                  },
                  label: function (tooltipItems) {
                    const value = tooltipItems.formattedValue;
                    return ` ${value}`;
                  },
                },
                titleFont: {
                  weight: 'bold',
                },
              },
            },
            elements: {
              line: {
                tension: 0,
              },
            },
            responsive: true,
          },
        });
      }
    },
    sortBy(type) {
      if (type == "lowest") {
        document.getElementById("sortby").innerHTML = this.$t(
          "insights.Lowest_Success_Score"
        );
      } else if (type == "highest") {
        document.getElementById("sortby").innerHTML = this.$t(
          "insights.Highest_Success_Score"
        );
      }
      this.$store.dispatch("dashboard/sortBy", type);
    },
    getImgUrlDim(pic) {
      var images = require.context(
        "../../assets/images/dimension/",
        false,
        /\.png$/
      );
      return images("./" + pic + ".png");
    },
    getImgUrl(pic) {
      var images = require.context("../../assets/images/", false, /\.png$/);
      return images("./" + pic + ".png");
    },
    showList() {
      this.$store.dispatch("dashboard/getDataTalkingPointsMonthly", this.user);
      this.activeSentiments = { positive: true, neutral: true, negative: true };
      this.currentViewportIndex = -1;
      this.monthContentHeights = {};
      this.animatedMonthKeys = {};
      // Initialize all months as visible to avoid placeholder flash on first render;
      // the IntersectionObserver will then manage visibility as the user scrolls
      const monthly = this.talking_points_monthly;
      const initVisible = {};
      if (monthly) {
        Object.keys(monthly).forEach(key => { initVisible[key] = true; });
      }
      this.visibleMonthKeys = initVisible;
      this.dialogVisible = true;
      this.$nextTick(() => {
        if (this.sentimentChartUnwatch) this.sentimentChartUnwatch();
        this.sentimentChartUnwatch = this.$watch('sentimentChartData', (val) => {
          if (val) {
            this.renderSentimentChart();
            this.$nextTick(() => {
              this.initViewportSync();
              this.setupMonthObserver();
            });
          }
        }, { immediate: true });
      });
    },
    renderSentimentChart() {
      const data = this.sentimentChartData;
      if (!data) return;
      const canvas = this.$refs.sentimentChart;
      if (!canvas) return;
      if (this.sentimentChartInstance) {
        this.sentimentChartInstance.destroy();
        this.sentimentChartInstance = null;
      }
      const ctx = canvas.getContext('2d');
      const neutralHalf = data.neutral.map(v => Math.ceil(v / 2));
      const neutralHalfLower = data.neutral.map(v => Math.floor(v / 2));
      const totalMonths = data.labels.length;
      this.sentimentChartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.labels,
          datasets: [
            {
              label: 'Positive',
              data: data.positive.map((v, i) => v + neutralHalf[i]),
              backgroundColor: this.activeSentiments.positive ? 'rgba(36, 157, 42, 0.45)' : 'rgba(200, 200, 200, 0.15)',
              borderColor: this.activeSentiments.positive ? '#249d2a' : '#ccc',
              borderWidth: 1.5,
              fill: 'origin',
              tension: 0.45,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointHoverBackgroundColor: '#249d2a',
              order: 2,
            },
            {
              label: 'Neutral (upper)',
              data: neutralHalf,
              backgroundColor: this.activeSentiments.neutral ? 'rgba(180, 180, 180, 0.3)' : 'rgba(200, 200, 200, 0.1)',
              borderColor: this.activeSentiments.neutral ? '#bbb' : '#ddd',
              borderWidth: 1,
              fill: 'origin',
              tension: 0.45,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHoverBackgroundColor: '#999',
              order: 3,
            },
            {
              label: 'Negative',
              data: data.negative.map((v, i) => -(v + neutralHalfLower[i])),
              backgroundColor: this.activeSentiments.negative ? 'rgba(244, 67, 54, 0.4)' : 'rgba(200, 200, 200, 0.15)',
              borderColor: this.activeSentiments.negative ? '#f44336' : '#ccc',
              borderWidth: 1.5,
              fill: 'origin',
              tension: 0.45,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointHoverBackgroundColor: '#f44336',
              order: 2,
            },
            {
              label: 'Neutral (lower)',
              data: neutralHalfLower.map(v => -v),
              backgroundColor: this.activeSentiments.neutral ? 'rgba(180, 180, 180, 0.3)' : 'rgba(200, 200, 200, 0.1)',
              borderColor: this.activeSentiments.neutral ? '#bbb' : '#ddd',
              borderWidth: 1,
              fill: 'origin',
              tension: 0.45,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHoverBackgroundColor: '#999',
              order: 3,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          onClick: (event, elements) => {
            if (this.viewportDragging) return;
            if (elements && elements.length > 0) {
              const idx = elements[0].index;
              const monthKey = data.keys[idx];
              this.scrollToMonth(monthKey);
            }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: (context) => {
                  const idx = context.dataIndex;
                  const pos = data.positive[idx];
                  const neu = data.neutral[idx];
                  const neg = data.negative[idx];
                  if (context.datasetIndex === 0) {
                    return this.$t('insights.Positive') + ': ' + pos + '  \u00b7  ' + this.$t('insights.Neutral') + ': ' + neu + '  \u00b7  ' + this.$t('insights.Critical') + ': ' + neg;
                  }
                  return null;
                },
                title: (items) => {
                  return items[0].label;
                },
              },
              filter: (item) => item.datasetIndex === 0,
            },
            datalabels: { display: false },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                color: '#999',
                font: { size: 10, family: 'Lato', weight: '600' },
                maxRotation: 0,
                minRotation: 0,
                autoSkip: true,
                maxTicksLimit: totalMonths > 24 ? 12 : (totalMonths > 12 ? 8 : totalMonths),
              },
              border: { display: false },
            },
            y: {
              display: false,
              stacked: false,
            },
          },
          interaction: {
            mode: 'index',
            intersect: false,
          },
          layout: {
            padding: { left: 0, right: 0, top: 4, bottom: 0 },
          },
        },
      });
      this.$nextTick(() => this.updateViewportBrush());
    },
    setupMonthObserver() {
      if (this.monthObserver) this.monthObserver.disconnect();
      const scrollEl = this.$refs.scrollContainer;
      if (!scrollEl) return;
      this.monthObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const key = entry.target.getAttribute('data-month-key');
          if (!key) return;
          if (entry.isIntersecting) {
            this.$set(this.visibleMonthKeys, key, true);
            // Mark as animated after first render to prevent re-animation on re-scroll
            this.$nextTick(() => { this.$set(this.animatedMonthKeys, key, true); });
          } else {
            const contentEl = entry.target.querySelector('.month-tags-content');
            if (contentEl) {
              this.$set(this.monthContentHeights, key, contentEl.offsetHeight);
            }
            this.$set(this.visibleMonthKeys, key, false);
          }
        });
      }, {
        root: scrollEl,
        rootMargin: '400px 0px',
      });
      this.$nextTick(() => {
        const allKeys = Object.keys(this.talking_points_monthly);
        allKeys.forEach(key => {
          const ref = this.$refs['month-' + key];
          if (!ref) return;
          const el = Array.isArray(ref) ? ref[0] : ref;
          if (el) {
            el.setAttribute('data-month-key', key);
            this.monthObserver.observe(el);
          }
        });
        // Mark initially visible months as animated after first render
        this.$nextTick(() => {
          allKeys.forEach(key => {
            if (this.visibleMonthKeys[key]) {
              this.$set(this.animatedMonthKeys, key, true);
            }
          });
        });
      });
    },
    destroyMonthObserver() {
      if (this.monthObserver) {
        this.monthObserver.disconnect();
        this.monthObserver = null;
      }
    },
    initViewportSync() {
      const scrollEl = this.$refs.scrollContainer;
      if (!scrollEl) return;
      this.teardownViewportSync();
      this.boundOnContentScroll = () => this.onContentScroll();
      scrollEl.addEventListener('scroll', this.boundOnContentScroll, { passive: true });
      this.onContentScroll();
    },
    teardownViewportSync() {
      if (this._scrollRafId) {
        cancelAnimationFrame(this._scrollRafId);
        this._scrollRafId = null;
      }
      const scrollEl = this.$refs.scrollContainer;
      if (scrollEl && this.boundOnContentScroll) {
        scrollEl.removeEventListener('scroll', this.boundOnContentScroll);
      }
      this.removeGlobalDragListeners();
    },
    onContentScroll() {
      if (this._scrollRafId) return;
      this._scrollRafId = requestAnimationFrame(() => {
        this._scrollRafId = null;
        this._doContentScroll();
      });
    },
    _doContentScroll() {
      if (this.viewportDragging) return;
      const data = this.sentimentChartData;
      if (!data) return;
      const scrollEl = this.$refs.scrollContainer;
      if (!scrollEl) return;
      const keys = data.keys;
      const containerTop = scrollEl.getBoundingClientRect().top;
      let closestIdx = 0;
      let closestDist = Infinity;
      for (let i = 0; i < keys.length; i++) {
        const ref = this.$refs['month-' + keys[i]];
        if (!ref) continue;
        const el = Array.isArray(ref) ? ref[0] : ref;
        const domEl = el && el.$el ? el.$el : el;
        if (!domEl) continue;
        const rect = domEl.getBoundingClientRect();
        const dist = Math.abs(rect.top - containerTop);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      }
      this.currentViewportIndex = closestIdx;
      this.updateViewportBrush();
    },
    updateViewportBrush() {
      const brush = this.$refs.viewportBrush;
      const chart = this.sentimentChartInstance;
      const data = this.sentimentChartData;
      if (!brush || !chart || !data) return;
      const totalMonths = data.keys.length;
      if (totalMonths === 0) return;
      const chartArea = chart.chartArea;
      if (!chartArea) return;
      const chartLeft = chartArea.left;
      const chartWidth = chartArea.right - chartArea.left;
      const segmentWidth = chartWidth / totalMonths;
      const visibleMonths = Math.max(1, Math.min(3, Math.ceil(totalMonths * 0.08)));
      const idx = Math.max(0, this.currentViewportIndex);
      const brushLeft = chartLeft + (idx * segmentWidth);
      const maxAvailableWidth = chartWidth - (idx * segmentWidth);
      const brushWidth = Math.min(Math.max(segmentWidth * visibleMonths, 24), maxAvailableWidth);
      brush.style.left = brushLeft + 'px';
      brush.style.width = brushWidth + 'px';
      brush.style.top = chartArea.top + 'px';
      brush.style.height = (chartArea.bottom - chartArea.top) + 'px';
      brush.style.display = 'block';
    },
    startViewportDrag(event) {
      this.viewportDragging = true;
      this.viewportDragStartX = event.clientX;
      this.viewportScrollStartLeft = this.currentViewportIndex;
      const brush = this.$refs.viewportBrush;
      if (brush) brush.classList.add('dragging');
      this.boundHandleViewportDrag = (e) => this.handleViewportDrag(e);
      this.boundStopViewportDrag = () => this.stopViewportDrag();
      document.addEventListener('mousemove', this.boundHandleViewportDrag);
      document.addEventListener('mouseup', this.boundStopViewportDrag);
    },
    handleViewportDrag(event) {
      if (!this.viewportDragging) return;
      const data = this.sentimentChartData;
      const chart = this.sentimentChartInstance;
      if (!data || !chart) return;
      const chartArea = chart.chartArea;
      if (!chartArea) return;
      const chartWidth = chartArea.right - chartArea.left;
      const totalMonths = data.keys.length;
      const dx = event.clientX - this.viewportDragStartX;
      const segmentWidth = chartWidth / totalMonths;
      const indexDelta = Math.round(dx / segmentWidth);
      const newIndex = Math.max(0, Math.min(totalMonths - 1, this.viewportScrollStartLeft + indexDelta));
      if (newIndex !== this.currentViewportIndex) {
        this.currentViewportIndex = newIndex;
        this.updateViewportBrush();
        const monthKey = data.keys[newIndex];
        this.scrollToMonth(monthKey, 'auto');
      }
    },
    stopViewportDrag() {
      this.viewportDragging = false;
      const brush = this.$refs.viewportBrush;
      if (brush) brush.classList.remove('dragging');
      this.removeGlobalDragListeners();
    },
    removeGlobalDragListeners() {
      if (this.boundHandleViewportDrag) {
        document.removeEventListener('mousemove', this.boundHandleViewportDrag);
        this.boundHandleViewportDrag = null;
      }
      if (this.boundStopViewportDrag) {
        document.removeEventListener('mouseup', this.boundStopViewportDrag);
        this.boundStopViewportDrag = null;
      }
    },
    scrollToMonth(monthKey, behavior) {
      const ref = this.$refs['month-' + monthKey];
      if (!ref) return;
      const el = Array.isArray(ref) ? ref[0] : ref;
      const domEl = el && el.$el ? el.$el : el;
      if (!domEl) return;
      const scrollEl = this.$refs.scrollContainer;
      if (scrollEl) {
        const containerRect = scrollEl.getBoundingClientRect();
        const elRect = domEl.getBoundingClientRect();
        const offset = elRect.top - containerRect.top + scrollEl.scrollTop;
        scrollEl.scrollTo({ top: offset, behavior: behavior || 'smooth' });
      }
    },
    toggleSentiment(type) {
      const active = this.activeSentiments;
      const activeCount = Object.values(active).filter(v => v).length;
      if (activeCount === 1 && active[type]) return;
      this.$set(this.activeSentiments, type, !active[type]);
      this.renderSentimentChart();
    },
    getSentimentType(item, monthKey) {
      if (monthKey < '2024-12') {
        if (item.sentiment > 0) return 'positive';
        if (item.sentiment < 0) return 'negative';
        return 'neutral';
      }
      if (item.sentiment === 'positive') return 'positive';
      if (item.sentiment === 'negative') return 'negative';
      return 'neutral';
    },
    destroySentimentChart() {
      this.teardownViewportSync();
      this.destroyMonthObserver();
      if (this.sentimentChartUnwatch) {
        this.sentimentChartUnwatch();
        this.sentimentChartUnwatch = null;
      }
      if (this.sentimentChartInstance) {
        this.sentimentChartInstance.destroy();
        this.sentimentChartInstance = null;
      }
    },
    setMonthly(MonthYear) {
      let split = MonthYear.split("-");
      let labelOpt = format(new Date(split[0], split[1] - 1, 1), "MMMM YYYY");
      return labelOpt;
    },
    getFontStyle(text) {
      // Thai characters
      if (text.match(/[ก-๙]/)) {
        return "Kanit";
      }
      // Chinese characters (Traditional Chinese)
      else if (text.match(/[\u4e00-\u9fff\u3400-\u4dbf]/)) {
        return "Noto Sans TC";
      }
      // Malay uses Latin script, so Lato is fine
      else {
        return "Lato";
      }
    },
    DimensionClicked(row) {
      this.$store.dispatch("dashboard/resetState");
      let data = {
        dimpic: row.dimpic,
        name: row.name,
        quest_id: row.quest_id,
      };
      this.dimensionData = data;
      this.$store.dispatch("dashboard/getDataChart", {
        user: this.user,
        quest_id: row.quest_id,
      });
      this.$store.dispatch("dashboard/getDataTalkingPoints30D", {
        user: this.user,
        category_id: row.category_id,
      });
      this.dialogDimensionVisible = true;
    },
    truncateText(text) {
      let length = this.maxLength;
      return text.length > length ? text.slice(0, length) + "..." : text;
    },
    truncateTextKeyGroup(text) {
      let length = 18;
      return text.length > length ? text.slice(0, length) + "..." : text;
    },
    activeLineHeight(value) {
      let percentage = (value / this.maximumUsers) * 100;
      return (40 / 100) * percentage;
    },
    arrayToPercentage(value) {
      const arr = [];
      let color = "bar-red";
      if (value > 70) {
        color = "bar-green";
      } else if (value > 40) {
        color = "bar-yellow";
      }
      for (let i = 0; i < 10; i++) {
        let status = (value - i * 10) * 1;
        let style = "";
        if (status > 10) {
          style = "";
        } else if (value - i * 10 > 0) {
          style = `opa-0${status * 1}`;
        } else {
          color = "";
        }
        arr.push(`${style} ${color}`);
      }
      return arr;
    },

    benchmarkBar(value) {
      if (this.checkedInxed()) {
        if (value < 0) {
          return "0";
        } else if (value > 99) {
          return "120";
        }
        return (value * 1.2).toFixed();
      } else {
        if (value < -99) {
          return "0";
        } else if (value > 99) {
          return "120";
        }
        return ((value + 100) * 0.6).toFixed();
      }
    },
    routerLink(path, tab, query) {
      if (path == "insightsWithTab" && tab) {
        // if (tab == "managers") {
        //   this.$store.commit("insightsManagers/setTabManager", true);
        // }
        this.$emit("changedTab", { path, tab });
      } else if (tab) {
        if (query) {
          this.$router.push({
            name: path,
            params: { tab: tab },
            query: { questionID: query },
          });
          this.$store.commit("dashboard/setOpenModalDashboard", true);
        } else {
          this.$router.push({ name: path, params: { tab: tab } });
        }
      } else {
        this.$message.error("Oops, Something wrong with this link.");
      }
    },
    navigateToPerformance(tab) {
      this.$router.push({ name: 'performance', params: { tab } });
    },
    navigateToSkills(tab) {
      this.$router.push({ name: 'skills', params: { tab } });
    },
    setEngagementImpactLoading() {
      if (
        this.impactRatingLoading == false &&
        this.enpsLoading == false
      ) {
        this.engagementImpactLoadingStatus = false;
      } else {
        this.engagementImpactLoadingStatus = true;
      }
    },
    setWeeklyLoadingStatus() {
      if (this.feedbackLoading == false && this.happinessLoading == false) {
        this.weeklyLoadingStatus = false;
      } else {
        this.weeklyLoadingStatus = true;
      }
    },
    benchmarkScore() {
      if (!this.checkedInxed()) {
        return 30;
      }
      return 80;
    },
    benchmarkScoreRed() {
      if (!this.checkedInxed()) {
        return 0;
      }
      return 50;
    },
    checkedInxed() {
      let index = this.benchmarkBreak != "eNPS" && this.benchmarkBreak != "NPS";

      if (index) {
        return true;
      }
      return false;
    },
    imagesBenchmark(value) {
      if (this.checkedInxed) {
        let benchmark = "Resilience";
        let type = "Critical";
        switch (this.benchmarkBreak) {
          case "WHO-5 Wellbeing Index":
            benchmark = "Wellness";
            break;
          case "Culture Index":
            benchmark = "Culture";
            break;
          case "Employee Resilience Index":
            benchmark = "Resilience";
        }
        if (value == "Excellent") {
          type = "Excellence";
        } else if (value == "Fair") {
          type = "Fair";
        }
        return `benchmark_${benchmark} - ${type}_16x16`;
      } else {
        if (value == "Excellent") {
          type = "Excellent";
          return "Promoters_16";
        } else if (value == "Fair") {
          return "Passives_16";
        }
        return "Distractors_16";
      }
    },
    detectColorTotal(type) {
      let value = 0;
      if(this.databarchartEngagementScore[type] && this.databarchartEngagementScore[type][this.databarchartEngagementScore[type].length - 1]){
        value = this.databarchartEngagementScore[type][this.databarchartEngagementScore[type].length - 1];
      }
      if(value >= 70){
        return "green";
      }else if(value < 70 && value >= 40){
        return "yellow";
      }else{
        if(this.databarchartEngagementScore[type] && this.databarchartEngagementScore[type][this.databarchartEngagementScore[type].length - 1]){
          return "red";
        }else{
          return "";
        }
      }
    },
    detectTextTotal(type) {
      let value = 0;
      if(this.databarchartEngagementScore[type] && this.databarchartEngagementScore[type][this.databarchartEngagementScore[type].length - 1]){
        value = this.databarchartEngagementScore[type][this.databarchartEngagementScore[type].length - 1];
      }
      if(value >= 70){
        return "GOOD";
      }else if(value < 70 && value >= 40){
        return "OKAY";
      }else{
        if(this.databarchartEngagementScore[type] && this.databarchartEngagementScore[type][this.databarchartEngagementScore[type].length - 1]){
          return "POOR";
        }else{
          return "-";
        }
      }
    },
    getScoreCount(score) {
      if (!this.enps || !this.enps.allScores) return 0;
      
      const scoreItem = this.enps.allScores.find(item => {
        return Number(item.score) === Number(score);
      });
      
      return scoreItem ? scoreItem.count : 0;
    },
    
    getScoreWidth(score) {
      if (!this.enps || !this.enps.allScores) return '0%';
      
      const count = this.getScoreCount(score);
      if (count === 0) return '0%';
      
      const counts = this.enps.allScores.map(item => item.count);
      const maxCount = counts.length > 0 ? Math.max(...counts) : 1;
      
      return `${(count * 100 / maxCount)}%`;
    },
    getScoreRange() {
      if (this.enps.type === 'WHO-5 Wellbeing Index') {
        // Return array [0, 10, 20, ..., 100] for WHO-5
        return Array.from({ length: 11 }, (_, i) => i * 10);
      }
      // Return array [0,1,2,...,10] for NPS/eNPS
      return Array.from({ length: 11 }, (_, i) => i);
    },

    formatScoreLabel(score) {
      if (this.enps.type === 'WHO-5 Wellbeing Index') {
        return score === 100 ? '100' : `${score}`;
      }
      return score.toString();
    },

    getScoreColor(score) {
      if (this.enps.type === 'WHO-5 Wellbeing Index') {
        // WHO-5 color scheme: red < 50, yellow 50-69, green >= 70
        return score >= 70 ? '#35d077' : score >= 50 ? '#fbc02d' : '#f44336';
      }
      // NPS/eNPS color scheme
      return score >= 9 ? '#35d077' : score <= 6 ? '#f44336' : '#fbc02d';
    },

    getTotalResponses() {
      if (!this.enps || !this.enps.allScores) return 0;
      
      // Use allScores array to calculate total responses
      if (this.enps.allScores && this.enps.allScores.length > 0) {
        return this.enps.allScores.reduce((sum, item) => sum + (item.count || 0), 0);
      }
      
      // Fallback to the old method if allScores is not available
      if (this.enps.type === 'WHO-5 Wellbeing Index') {
        return this.enps.excellent + this.enps.fair + this.enps.critical || 0;
      }
      return this.enps.promotors + this.enps.passives + this.enps.detractors || 0;
    },
    getPeopleImpactClass() {
      // Check if data is still loading or invalid
      if (!this.engagementpeople || !this.engagementpeople.total || this.engagementpeople.total === 0) {
        return 'highlight-text'; // No color class when loading
      }
      
      const percentage = Math.round((this.engagementpeople.counts.green + this.engagementpeople.counts.yellow) / this.engagementpeople.total * 100);
      if (isNaN(percentage)) {
        return 'highlight-text'; // No color class for invalid data
      }
      
      if (percentage >= 70) {
        return 'highlight-text highlight-green';
      } else if (percentage >= 40) {
        return 'highlight-text highlight-yellow';
      } else {
        return 'highlight-text highlight-red';
      }
    },
    getPeopleImpactText() {
      // Check if data is still loading or invalid
      if (!this.engagementpeople || !this.engagementpeople.total || this.engagementpeople.total === 0) {
        return 'Loading...';
      }
      
      const percentage = Math.round((this.engagementpeople.counts.green + this.engagementpeople.counts.yellow) / this.engagementpeople.total * 100);
      if (isNaN(percentage)) {
        return 'Loading...';
      }
      
      return `${percentage}% impactful`;
    },
    getManagerActivityClass() {
      // Check if data is still loading or invalid
      if (!this.activeOverview || !this.activeOverview.all || this.activeOverview.all === 0) {
        return 'highlight-text'; // No color class when loading
      }
      
      const percentage = Math.round((this.activeOverview.active + this.activeOverview.passive) / this.activeOverview.all * 100);
      if (isNaN(percentage)) {
        return 'highlight-text'; // No color class for invalid data
      }
      
      if (percentage >= 80) {
        return 'highlight-text highlight-green';
      } else if (percentage >= 50) {
        return 'highlight-text highlight-yellow';
      } else {
        return 'highlight-text highlight-red';
      }
    },
    getManagerActivityText() {
      // Check if data is still loading or invalid
      if (!this.activeOverview || !this.activeOverview.all || this.activeOverview.all === 0) {
        return 'Loading...';
      }
      
      const percentage = Math.round((this.activeOverview.active + this.activeOverview.passive) / this.activeOverview.all * 100);
      if (isNaN(percentage)) {
        return 'Loading...';
      }
      
      return `${percentage}% replying`;
    },
    getMiniTrend(questId) {
      const points = (this.miniTrends && this.miniTrends[questId]) ? this.miniTrends[questId] : [];
      // Only keep last 3-4
      if (points.length > 4) return points.slice(points.length - 4);
      return points;
    },
    computeSparkline(points) {
      if (!points || points.length === 0) return '';
      const w = 40, h = 16, padX = 1, padY = 1;
      const n = points.length;
      const min = Math.min(...points);
      const max = Math.max(...points);
      const range = max - min || 1;
      const stepX = (w - padX * 2) / (n - 1);
      const scaled = points.map((v, i) => {
        const x = padX + i * stepX;
        const y = padY + (h - padY * 2) * (1 - (v - min) / range);
        return `${x},${y}`;
      });
      return scaled.join(' ');
    },
    sparkColor(points) {
      if (!points || points.length < 2) return '#6c30cc';
      const diff = points[points.length - 1] - points[points.length - 2];
      return diff >= 0 ? '#27bc6c' : '#f44336';
    },
  },
};
</script>
<style scoped lang="scss">
#insightsDashboard>>>img {
  border-radius: 50%;
}

.flex.card {
  justify-content: center;
}

.menuOptionStyle {
  border-bottom: transparent;
  background: #f4f4f4;
  border-radius: 4px;
}

.divTable {
  flex: 1;
  margin-left: -9px;
}

.txtDetailTable {
  color: #9d9d9d;
  font-size: 14px;
  word-break: normal;
}

// .card-box {
//   margin-top: 20px;
//   margin-right: 20px;
//   width: 180px;
//   height: 100px;
//   border-radius: 5px;
//   background-color: #f4f4f4;
//   text-align: center;
//   box-sizing: border-box;
// }
.KeyGroupBox {
  margin-top: 6px;
  margin-right: 6px;
  width: 168px;
  height: 48px;
  border-radius: 8px;
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  // box-shadow: 0px 2px 4px #00000029;
  box-sizing: border-box;
  overflow: hidden;
  cursor: default;
}

#insightsDashboard .KeyGroupBox img {
  border-radius: 0;
}

// .txtHeadBox {
//   padding: 21px 5px 2px 5px;
//   font-size: 36px;
//   color: #363636;
//   display: inline-block;
// }
.txtHeaderKey {
  font-size: 12px;
  color: #535353;
  font-weight: 700;
  white-space: nowrap;
}

.txtDetailKey {
  font-size: 10px;
  color: #000000;
  opacity: 0.5;
}

.txtPercent {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #999999;
}

.txtPercent.green {
  color: #27bc6c;
}

.txtPercent.red {
  color: #f44336;
}
.success-score-cell {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.success-score-cell .score-and-change {
  display: flex;
  align-items: baseline;
}

.success-score-cell .mini-trend svg {
  display: block;
}


.txtPercent.grey {
  color: #747474;
}

.divCurcleStatus {
  margin-left: 5px;
  margin-right: 5px;
  width: 50px;
  height: 48px;
  text-align: center;
  border-radius: 50%;
}

.divCurcleStatus.green {
  background-color: #66bb6a;
}

.divCurcleStatus.yellow {
  background-color: #fbc02d;
}

.divCurcleStatus.red {
  background-color: #f44336;
}

.divCurclePic {
  margin-left: 5px;
  margin-right: 5px;
  width: 60px;
  height: 60px;
  text-align: center;
  border-radius: 50%;
  background-color: #f0f0f0;
}

.txtStatus {
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  padding: 0px;
  line-height: 50px;
}

.txtButton {
  padding: 0;
  margin-left: 16px;
  font-size: 14px;
}

.el-button--text {
  color: #6c30cc;
}

.el-button--text:hover {
  color: #5423a2;
}

.el-button--text:active {
  color: #6c30cc;
}

.talkingPointsBox {
  height: 40px;
}

.talking-points {
  border-width: 0;
  border-radius: 16px;
  font-weight: 700;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  margin: 8px 8px 0 0;

  &:hover {
    transform: scale(1.03);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.GreenBoxStyle {
  color: #249d2a;
  background-color: #edffee;
}

.RedBoxStyle {
  color: #f44336;
  background-color: #ffedeb;
}

.GreyBoxStyle {
  color: #484848;
  // border-color: #b2b2b2;
  background-color: #f0f0f0;
}

.buttonClose {
  padding-top: 10px;
  height: 36px;
  font-size: 16px;
  font-weight: 700;
}

.headerModalStyle {
  margin-left: 20px;
  margin-right: 52px;
  margin-top: 30px;
}

.txtMonthlyStyle {
  color: #747474;
  font-size: 16px;
  font-weight: 700;
}

.scroll-style {
  max-height: 45vh;
  padding-bottom: 1px;
  overflow-y: auto;
}

// Sentiment stream chart container
.sentiment-chart-container {
  padding: 16px 52px 12px;
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
}

.sentiment-chart-wrapper {
  position: relative;
  overflow: hidden;
}

// Viewport brush overlay
.viewport-brush {
  position: absolute;
  display: none;
  background: rgba(108, 48, 204, 0.10);
  border: 2px solid rgba(108, 48, 204, 0.35);
  border-radius: 4px;
  cursor: grab;
  z-index: 2;
  transition: left 0.15s ease, width 0.15s ease;
  user-select: none;

  &:hover {
    background: rgba(108, 48, 204, 0.16);
    border-color: rgba(108, 48, 204, 0.5);
  }

  &:active {
    cursor: grabbing;
    background: rgba(108, 48, 204, 0.20);
    border-color: rgba(108, 48, 204, 0.6);
    transition: none;
  }
}

.viewport-brush.dragging {
  transition: none !important;
  background: rgba(108, 48, 204, 0.22);
  border-color: rgba(108, 48, 204, 0.6);
  cursor: grabbing;
}

.viewport-brush-handle {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 20px;
  border-radius: 2px;
  background: rgba(108, 48, 204, 0.4);

  &.left { left: 2px; }
  &.right { right: 2px; }
}

.month-tags-content {
  min-height: 40px;
}

.month-tags-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, rgba(0,0,0,0.01) 0%, rgba(0,0,0,0.02) 50%, rgba(0,0,0,0.01) 100%);
  border-radius: 8px;
  margin: 4px 0;
}

// Sentiment breakdown bar per month
.sentiment-breakdown {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 8px 52px 16px;
}

.sentiment-bar {
  display: flex;
  width: 100%;
  max-width: 500px;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
}

.sentiment-bar-segment {
  transition: width 0.4s ease;
  &.positive { background: #249d2a; }
  &.neutral { background: #c0c0c0; }
  &.negative { background: #f44336; }
}

.sentiment-summary {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
  letter-spacing: 0.02em;
}

// Legend filter states
.legend-tag-inactive {
  opacity: 0.3;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.legend-tag-active {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

// Tag visibility based on sentiment filter
.tag-filtered-out {
  opacity: 0.12;
  transform: scale(0.95);
  transition: opacity 0.3s ease, transform 0.3s ease;
}

// Staggered tag entrance animation
.tag-enter {
  animation: tagFadeIn 0.3s ease forwards;
  opacity: 0;
}

@keyframes tagFadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.scrollingStyle2 {
  max-height: 55vh;
}

.txtDimensionDesc {
  color: #626262;
  font-size: 14px;
}

.barchart-style {
  background-color: rgba(0, 0, 0, 0.03);
  // box-shadow: 0px 2px 4px #00000029;
  border-radius: 24px;
  margin-top: 10px;
  width: calc(100% - 42px);
  height: 186px;
  padding: 10px 24px 12px 18px;
}

.chart-legend {
  display: flex;
  padding: 4px 12px;
  border: 1px solid #E8E8E8;
  border-radius: 4px;
  align-items: center;
  margin: 6px 0 0 12px;
}

.chart-legend .symbol {
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin: 0 8px;
}

.symbol.blue {
  background-color: #00ACC1;
}

.symbol.orange {
  background-color: #E45006;
}

.symbol.pink {
  background-color: #E048DA;
}

.chart-legend .tag {
  margin-left: 8px;
  padding: 1px 8px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 4px;
}

.chart-legend .tag.yellow {
  background-color: rgba(251, 192, 45, .20);
  color: #C3931E;
}

.chart-legend .tag.red {
  background-color: rgba(244, 67, 54, .20);
  color: #C73A2F;
}

.chart-legend .tag.green {
  background-color: rgba(102, 187, 106, .20);
  color: #559B58;
}

.barchart-engagement-style {
  width: calc(100% - 12px);
  height: 120px;
}

.chartStyle {
  padding: 16px;
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 24px;
}

.tooltipStyle {
  width: 375px;
  padding: 8px;
}

/* จัดการรายการภายใน Tooltip */
.tooltip-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* สไตล์ตัวเลขวงกลม (OI) */
.oi-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
  border-radius: 50%;
  background-color: rgba(108, 48, 204, 0.2); /* สีพื้นหลัง */
  // opacity: 0.2;
  color: #6C30CC; /* สีตัวอักษร */
  font-weight: bold;
  margin-right: 12px;
}

/* ข้อความ */
.tooltip-item {
  display: flex;
  align-items: flex-start;
  padding-top : 11px;
}

.tooltip-item p {
  margin: 0;
  font-size: 12px;
}

.tooltipHelpStyle {
  width: 218px;
}

.tooltipKeyGroupStyle {
  max-width: 250px;
}

.grid-2-second {
  display: grid;
  grid-template-columns: 40% 60%;
}

.icon-beta {
  padding: 5px 12px 3px;
  border-radius: 12px;
  background-color: #1ec182;
  color: #fff;
  font-size: 10px;
  font-weight: 400;
  margin-left: 12px;
}

.tone-body {
  margin-top: 14px;

  .arrow {
    height: 14px;
    width: 20px;

    >img {
      height: 12px;
      width: 12px;
      margin-top: 8px;
    }
  }

  .number {
    width: 28px;
    margin-top: 3px;
  }

  .emoji {
    width: 120px;
  }

  .percentage-bar {
    width: 80px;
    margin-top: 7px;
  }

  .percentage-number {
    width: 35px;
    text-align: end;
    margin-left: 4px;
    margin-top: 2px;
  }

  .percentage-change-number {
    margin-left: 10px;
    text-align: center;
    border-radius: 4px;
    font-size: 12px;
    padding: 4px 4px 2px;
  }

  .percentage-change-number.up {
    background-color: #12c29a;
    color: #fff;
  }

  .percentage-change-number.down {
    background-color: #e2e2e2;
    color: #5c5c5c;
  }
  
  .percentage-change-number.same {
    background-color: #f5f5f5;
    color: #666;
  }
}

.quality-check-container {
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 8px;
  padding: 20px 24px;
  margin-top: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  .quality-counters-inline {
    display: flex;
    gap: 40px;
    align-items: center;
    justify-content: flex-start;
  }
  
  .quality-counter-inline {
    display: flex;
    align-items: center;
    
    .counter-emoji {
      width: 24px;
      margin-right: 10px;
      text-align: center;
      font-size: 16px;
    }
    
    .counter-label {
      width: 90px;
      margin-right: 16px;
      font-weight: 500;
    }
    
    .counter-value {
      padding: 4px 12px;
      border-radius: 6px;
      margin-right: 12px;
      min-width: 40px;
      text-align: center;
      font-weight: 700;
      font-size: 15px;
      
      &.success {
        background-color: #ecfdf5;
        color: #059669;
        border: 1px solid #bbf7d0;
      }
      
      &.warning {
        background-color: #fefbeb;
        color: #d97706;
        border: 1px solid #fde68a;
      }
      
      &.danger {
        background-color: #fef2f2;
        color: #dc2626;
        border: 1px solid #fecaca;
      }
    }
    
    .counter-trend {
      opacity: 0.9;
      font-weight: 700;
      font-size: 13px;
      padding: 2px 6px;
      border-radius: 4px;
      
      .trend-down {
        color: #059669;
        background: rgba(5, 150, 105, 0.1);
      }
      
      .trend-up {
        color: #dc2626;
        background: rgba(220, 38, 38, 0.1);
      }
      
      .trend-same {
        color: #6b7280;
        background: rgba(107, 114, 128, 0.1);
      }
    }
  }
}

.tooltip-quality-detail {
  max-width: 480px;
  line-height: 1.4;
  padding: 16px;
  background: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  border-radius: 8px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
  color: #1f2937 !important;
  
  .text-success {
    color: #12c29a;
  }
  
  .flex-center {
    display: flex;
    align-items: center;
  }
  
  .ml-auto {
    margin-left: auto;
  }
  
  .mb-4 {
    margin-bottom: 16px;
  }
  
  .mt-8 {
    margin-top: 16px;
  }
  
  .fs-11 {
    font-size: 11px;
  }
  
  .fc-6A6A6A {
    color: #6A6A6A;
  }
  
  .team-list-container {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.02);
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.05);
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
      border-radius: 3px;
      
      &:hover {
        background: rgba(0, 0, 0, 0.3);
      }
    }
  }
  
  .team-breakdown-item {
    display: grid;
    grid-template-columns: 40px 1fr 50px;
    align-items: center;
    padding: 8px 12px;
    font-size: 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    transition: background-color 0.2s ease;
    
    &:last-child {
      border-bottom: none;
    }
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.03);
    }
    
    .team-rank {
      font-weight: 600;
      color: #6b7280;
      text-align: center;
    }
    
    .team-name {
      color: #1f2937;
      padding: 0 8px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .team-count {
      text-align: right;
      font-weight: 600;
      color: #6b7280;
      background: rgba(108, 48, 204, 0.08);
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 11px;
    }
    
    &:nth-child(1) {
      .team-rank {
        color: #ef4444;
      }
      .team-count {
        background: rgba(239, 68, 68, 0.1);
        color: #ef4444;
      }
    }
    
    &:nth-child(2), &:nth-child(3) {
      .team-rank {
        color: #f59e0b;
      }
      .team-count {
        background: rgba(245, 158, 11, 0.1);
        color: #f59e0b;
      }
    }
  }
  
  .expand-button {
    color: #6c30cc !important;
    font-size: 11px !important;
    padding: 4px 8px !important;
    
    &:hover {
      color: #5a25aa !important;
    }
    
    i {
      margin-left: 4px;
      font-size: 10px;
    }
  }
}

.grid-2-tenure {
  display: grid;
  grid-template-columns: 652px auto;
}

.grid-3-engagement {
  display: grid;
  grid-template-columns: 33% 33% 33%;
}

.past-12-hours {
  .card {
    padding: 14px 16px 20px;
    margin-top: 8px;
    height: calc(200px - 32px + 6px);
    color: #fff;
    border-radius: 24px;
    background-color: #6c30cc;
    // box-shadow: 0px 2px 4px #00000029;
  }

  .active-bar {
    background-color: #35d077;
    width: 6px;
    margin-left: 2px;
    transition: 0.3s;

    &:hover {
      background-color: #16b65b;
    }
  }

  .line {
    height: 1px;
    background-color: #fff;
    opacity: 0.2;
    margin-top: 4px;
    margin-bottom: 9px;
  }
}

.top-contariner {

  // display: grid;
  // grid-template-columns: auto auto auto;
  .x-line {
    width: 100%;
    height: 1px;
    background-color: rgba($color: #000000, $alpha: 0.1);
  }

  .card-contariner {
    background: #ffffff 0% 0% no-repeat padding-box;
    box-shadow:
      0px 0.3px 0.9px rgba(0, 0, 0, 0.008),
      0px 0.8px 2.1px rgba(0, 0, 0, 0.012),
      0px 1.5px 4px rgba(0, 0, 0, 0.015),
      0px 2.7px 7.1px rgba(0, 0, 0, 0.018),
      0px 5px 13.4px rgba(0, 0, 0, 0.022),
      0px 12px 32px rgba(0, 0, 0, 0.03);
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.04);
    padding: 24px 20px 16px;
    margin-top: 8px;
    height: 234px;
    transition: box-shadow 0.3s ease;

    &:hover {
      box-shadow:
        0px 0.4px 1.1px rgba(0, 0, 0, 0.01),
        0px 1px 2.6px rgba(0, 0, 0, 0.015),
        0px 1.9px 5px rgba(0, 0, 0, 0.018),
        0px 3.4px 8.9px rgba(0, 0, 0, 0.022),
        0px 6.3px 16.8px rgba(0, 0, 0, 0.027),
        0px 15px 40px rgba(0, 0, 0, 0.035);
    }
  }

  .label-change {
    font-size: 11px;
    font-weight: 700;
    margin-top: 4px;
    margin-left: 3px;
    white-space: nowrap;
  }

  // .engagement,
  // .weekly-status,
  // .activity {
  // }
  .engagement {
    width: calc(100% - (460px + 32px));



    .engagement-left {
      width: calc(50% - 8px);
      padding-right: 12px;
      border-right: 1px solid rgba($color: #000000, $alpha: 0.08);
      position: relative;
      overflow: hidden;

      &::after {
        content: '';
        position: absolute;
        right: -1px;
        top: 20%;
        bottom: 20%;
        width: 1px;
        background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.1), transparent);
      }
    }

    .engagement-right {
      width: calc(50% - 8px);
      padding-left: 16px;
      position: relative;
      overflow: hidden;


    }

    .benchmark {
      width: 46px;
      text-align: center;
      padding: 10px 0 8px;
      font-size: 16px;
      font-weight: 700;
      color: #fff;
      border-radius: 4px;
      margin-right: 16px;
      background-color: #e0e0e0;
      cursor: help;
    }

    .benchmark.green {
      background-color: #35d077;
    }

    .benchmark.red {
      background-color: #f44336;
    }

    .benchmark.yellow {
      background-color: #fbc02d;
    }

          .tooltip-legend-item {
        display: flex;
        align-items: flex-start;
        margin: 8px 0;
        
        .tooltip-color {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          margin-right: 10px;
          margin-top: 2px;
          flex-shrink: 0;
          
          &.bg-green {
            background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
          }
          
          &.bg-yellow {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
          }
          
          &.bg-red {
            background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
          }
        }
      
      > div {
        flex: 1;
        line-height: 1.3;
        
        strong {
          display: block;
          margin-bottom: 2px;
        }
      }
    }

    .section-header {
      margin-bottom: 8px;
      
      .section-title {
        font-size: 13px;
        font-weight: 600;
        gap: 4px;
        transition: color 0.2s ease;
        
        &:hover {
          color: #6c30cc;
        }
        
        i {
          font-size: 9px;
          opacity: 0.7;
        }
      }
    }

    .metric-summary {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      margin-top: 6px;
      
      .highlight-text {
        font-weight: 600;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 12px;
        white-space: nowrap;
        
        &.highlight-green {
          color: #059669 !important;
          background: rgba(5, 150, 105, 0.1) !important;
        }
        
        &.highlight-yellow {
          color: #d97706 !important;
          background: rgba(217, 119, 6, 0.1) !important;
        }
        
        &.highlight-red {
          color: #dc2626 !important;
          background: rgba(220, 38, 38, 0.1) !important;
        }
      }
    }

    .stacked-bar-container {
      margin: 0 0 8px 0;

      .stacked-bar {
        display: flex;
        width: 100%;
        height: 20px;
        border-radius: 4px;
        overflow: hidden;
        background-color: #f8f9fa;
        margin-bottom: 6px;
        box-shadow: 
          0 2px 4px rgba(0, 0, 0, 0.06),
          inset 0 1px 2px rgba(0, 0, 0, 0.05);
        border: 1px solid rgba(0, 0, 0, 0.04);

        .bar-segment {
          height: 100%;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: help;
          position: relative;

          &:hover {
            filter: brightness(1.1);
            transform: scaleY(1.08);
            z-index: 1;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          }

          &:first-child {
            border-radius: 4px 0 0 4px;
          }

          &:last-child {
            border-radius: 0 4px 4px 0;
          }

          &:only-child {
            border-radius: 4px;
          }

          // Add subtle borders between segments
          &:not(:last-child)::after {
            content: '';
            position: absolute;
            right: 0;
            top: 4px;
            bottom: 4px;
            width: 1px;
            background: rgba(255, 255, 255, 0.3);
          }
        }

        .bar-green {
          background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
        }

        .bar-yellow {
          background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
        }

        .bar-red {
          background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
        }
      }

      .bar-legend {
        display: flex;
        justify-content: space-around;
        align-items: center;
        padding: 2px 4px;
        background: rgba(0, 0, 0, 0.02);
        border-radius: 8px;
        overflow: hidden;
        flex-wrap: nowrap;

        .legend-item {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 2px 4px;
          border-radius: 4px;
          transition: background-color 0.2s ease;
          flex: 1;
          justify-content: center;
          min-width: 0;

          &:hover {
            background: rgba(0, 0, 0, 0.04);
          }

          .legend-color {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            border: 1px solid rgba(255, 255, 255, 0.8);
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
            flex-shrink: 0;

            &.bg-green {
              background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
            }

            &.bg-yellow {
              background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            }

            &.bg-red {
              background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
            }
          }

          .legend-text {
            font-size: 10px;
            font-weight: 500;
            color: #6b7280;
            text-align: center;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }

    .point-bar {
      width: 120px;
      border-radius: 6px;
      height: 6px;
      background-color: rgba($color: #000000, $alpha: 0.2);

      .value {
        position: relative;
        border-radius: 6px;
        height: 6px;
        background: #35d077;
      }

      .value.benchmark {
        position: relative;
        height: 6px;
        top: -6px;
        background: transparent;

        &::before {
          content: "";
          position: absolute;
          right: -1px;
          top: -3px;
          border-right: 2px solid #6c30cc;
          height: 12px;
        }
      }
    }
  }

  .weekly-status {
    margin-left: 16px;
    width: 230px;
  }

  .activity {
    margin-left: 16px;
    width: 230px;

    .bar-contariner {
      .bar {
        border-radius: 6px;
        width: 6px;
        height: 24px;
        margin-right: 2px;
        background-color: rgba($color: #000000, $alpha: 0.1);
      }

      .bar-green {
        background-color: #35d077;
      }

      .bar-yellow {
        background-color: #fbc02d;
      }

      .bar-red {
        background-color: #f44336;
      }
    }
  }
}

.quality-feedback-tooltip {
  min-width: 480px !important;
  max-width: 600px !important;
  
  .el-tooltip__popper {
    padding: 0 !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }
}

.score-distribution-tooltip {
  min-width: 280px !important;
  padding: 16px !important;
  background: #2d2d2d !important;
  border-radius: 4px !important;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.2) !important;

  .tooltip-title {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #000000;
    text-align: left;
  }

  .score-bars {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .score-bar-item {
      display: grid;
      grid-template-columns: 30px 30px 1fr;
      align-items: center;
      height: 20px;

      .score-info {
        display: contents;

        .score-label {
          font-size: 12px;
          font-weight: 500;
          color: rgba(255,255,255,0.7);
          padding-right: 0px;
        }

        .score-count {
          font-size: 11px;
          color: rgba(255,255,255,0.5);
        }
      }

      .score-bar-wrapper {
        position: relative;
        background-color: rgba(255,255,255,0.1);
        border-radius: 2px;
        height: 3px;
        margin-top: 2px;

        .score-bar {
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          transition: width 0.3s ease;
          border-radius: 2px;
          width: 4px;
        }
      }
    }
  }
}

.horizontal-histogram {
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.score-labels, .histogram-bars, .score-counts {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
}

.score-labels {
  margin-bottom: 4px;
}

.score-counts {
  margin-top: 4px;
}

.score-label-item, .score-count-item {
  flex: 1;
  text-align: center;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.7);
  font-weight: 500;
}

.score-count-item {
  color: rgba(0, 0, 0, 0.5);
  font-size: 10px;
}

.histogram-bars {
  height: 120px;
  align-items: flex-end;
  margin: 10px 0;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.1);
  }
  
  /* Add horizontal grid lines for better readability */
  &::after {
    box-shadow: 0 30px 0 rgba(0, 0, 0, 0.03),
                0 60px 0 rgba(0, 0, 0, 0.03),
                0 90px 0 rgba(0, 0, 0, 0.03);
  }
}

.histogram-bar {
  flex: 1;
  margin: 0 6px;
  min-height: 1px;
  transition: height 0.3s ease, transform 0.2s ease;
  width: 8px;
  border-radius: 4px 4px 0 0;
  position: relative;
  
  &:hover {
    transform: scaleY(1.05);
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
    z-index: 1;
  }
  
  &::after {
    content: attr(data-value);
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 10px;
    color: rgba(0, 0, 0, 0.7);
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
}

// Focus & Goal Alignment Section Styles
.focus-alignment-section {
  width: 100%;
}

.section-headers-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  padding-bottom: 8px;
}

.section-header-group {
  display: flex;
  align-items: center;
}

.section-header-focus {
  // Spans first two columns worth
}

.section-header-learning {
  // Spans last column
}

.focus-alignment-cards {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-top: 8px;
}

.focus-card {
  background: #fff;
  border-radius: 6px;
  padding: 14px 18px;
  box-shadow:
    0px 0.3px 0.9px rgba(0, 0, 0, 0.008),
    0px 0.8px 2.1px rgba(0, 0, 0, 0.012),
    0px 1.5px 4px rgba(0, 0, 0, 0.015),
    0px 2.7px 7.1px rgba(0, 0, 0, 0.018),
    0px 5px 13.4px rgba(0, 0, 0, 0.022),
    0px 12px 32px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow:
      0px 0.4px 1.1px rgba(0, 0, 0, 0.01),
      0px 1px 2.6px rgba(0, 0, 0, 0.015),
      0px 1.9px 5px rgba(0, 0, 0, 0.018),
      0px 3.4px 8.9px rgba(0, 0, 0, 0.022),
      0px 6.3px 16.8px rgba(0, 0, 0, 0.027),
      0px 15px 40px rgba(0, 0, 0, 0.035);
  }
}

.focus-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.focus-card-loading {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.focus-card-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.focus-metric {
  display: flex;
  align-items: center;
}

.weekly-change-wrapper {
  display: flex;
  flex-direction: column;
  margin-left: 16px;
  padding-left: 16px;
  border-left: 1px solid #E4E7ED;
  line-height: 1.1;
}

.weekly-metric-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.weekly-value {
  font-size: 14px;
  font-weight: 700;
  color: #303133;
}

.change-label {
  font-size: 10px;
  color: #909399;
  font-weight: 500;
}

.focus-metric-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}

.focus-metric-na {
  color: #909399 !important;
}

.focus-progress-bar {
  width: 100%;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 6px;
}

.focus-progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s ease, background-color 0.3s ease;
}

.focus-metric-label {
  font-size: 11px;
  color: #606266;
  margin-top: 2px;
}

.learning-stats {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px !important;
}

.learning-stat {
  display: flex;
  align-items: center;
  gap: 3px;
}

.learning-stat-value {
  font-weight: 600;
  color: #303133;
}

.learning-stat-separator {
  color: #dcdfe6;
}

.focus-na-message {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
  
  i {
    font-size: 11px;
    color: #c0c4cc;
  }
}

// Week-over-week change indicator styles (inline with main score)
.label-change {
  font-size: 11px;
  font-weight: 700;
  margin-left: 0;
}

.text-green {
  color: #35D077;
}

.text-red {
  color: #F44336;
}

.fc-6A6A6A {
  color: #6A6A6A;
}

</style>

<!-- Global styles for tooltip content -->
<style>
<style>
.tooltip-emoji-detail {
  max-width: 300px;
}

/* Global tooltip color indicators for Element UI tooltips */
.tooltip-legend-item {
  display: flex;
  align-items: flex-start;
  margin: 8px 0;
}

.tooltip-color {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 10px;
  margin-top: 2px;
  flex-shrink: 0;
}

.tooltip-color.bg-green {
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
}

.tooltip-color.bg-yellow {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.tooltip-color.bg-red {
  background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
}

/* Team breakdown styles in tooltips */
.team-list-container {
  max-height: 200px;
  overflow-y: auto;
}

.team-breakdown-item {
  display: flex;
  align-items: center;
  padding: 4px 0;
  font-size: 12px;
}

.team-rank {
  width: 20px;
  color: #999;
  font-weight: 500;
  flex-shrink: 0;
}

.team-name {
  flex: 1;
  margin: 0 8px;
  color: #333;
}

.team-count {
  font-weight: 600;
  color: #666;
  flex-shrink: 0;
}

.tooltip-quality-detail {
  padding: 12px;
  max-width: 320px;
}

.expand-button {
  padding: 0;
  font-size: 11px;
  color: #6c30cc;
}

.text-success {
  color: #22c55e;
}

#insightsDashboard>div>div:nth-child(1)>div>div>input {
  height: 52px !important;
  font-size: 18px;
  font-weight: 400;
  color: #4a4a4a;
}

#insightsDashboard>div>div>div.showOption>ul>li>div {
  height: 40px !important;
  line-height: 40px !important;
}

#insightsDashboard .el-dialog {
  width: 1024px;
}

#insightsDashboard .el-dialog__body {
  padding: 10px 20px;
}

#insightsDashboard .el-dialog__footer {
  padding-top: 0px;
}
</style>
